import React from 'react';
import { createPortal } from 'react-dom';
import type { CombatUiStatusPulseKey, GameState } from '../game/types';
import StatusChips, {
  type EnemyStatusSnapshot,
  type PlayerStatusSnapshot,
} from './StatusChips';
import Toast, { type ToastMessage } from "./Toast";
import CardView from './CardView';
import CombatLog from './CombatLog';
import CombatUI from './CombatUI';
import Hand from './Hand';
import RelicBar from './RelicBar';
// Import the extracted prompt and bottom HUD components. These encapsulate
// the modal prompts and the lower combat HUD respectively.
import CombatPromptLayer from './CombatPromptLayer';
import CombatBottomHud from './CombatBottomHud';
import CombatBackdropFx from './CombatBackdropFx';
import cultistPortrait from '../assets/enemy-portrait.webp';
import hexbinderPortrait from '../assets/enemy-portrait-hexbinder.webp';
import nestedHorrorPortrait from '../assets/enemy-portrait-nested-horror.webp';
import dreadbrandPortrait from '../assets/enemy-portrait-dreadbrand.webp';
import corruptedCultistPortrait from '../../assets/enemy_corrupted_cultist.png';
import yahakaPortrait from '../../assets/enemy_yahaka.png';
import yahakaPortrait2 from '../../assets/enemy_yahaka_2.png';
import yahakaPortrait3 from '../../assets/enemy_yahaka_3.png';
import yahakaPortrait4 from '../../assets/enemy_yahaka_4.png';
import chainedPenitentPortrait from '../../assets/enemy_chained_penitent.png';
import chainedPenitentPortrait2 from '../../assets/enemy_chained_penitent_2.png';
import monsignorPortrait from '../../assets/enemy_monsignor.png';
import darkApostlePortrait from '../../assets/enemy_dark_apostle.png';
import chymistPortrait from '../../assets/enemy_chymist.png';
import mercenaryPortrait from '../../assets/enemy_mercenary.png';
import lampKeeperPortrait from '../../assets/enemy_lamp_keeper.png';
import thresholdVerifierPortrait from '../../assets/enemy_threshold_verifier.png';
import custodianPortrait from '../../assets/enemy_custodian.png';
import blackhandPortrait from '../../assets/enemy_blackhand.png';
import primeVesselPortrait from '../../assets/enemy_prime_vessel.png';
import primeCrucibleDevicePortrait from '../../assets/machine_prime_crucible_1.png';
import pulseEngineDevicePortrait from '../../assets/machine_pulse_engine.png';
import synapticRetortDevicePortrait from '../../assets/machine_synaptic_retort.png';
import astralReliquaryDevicePortrait from '../../assets/machine_astral_reliquary.png';
import primeVesselOverlayPrimeCrucibleImg from '../../assets/enemy_prime_vessel_1.png';
import primeVesselOverlaySynapticRetortImg from '../../assets/enemy_prime_vessel_2.png';
import primeVesselOverlayPulseEngineImg from '../../assets/enemy_prime_vessel_3.png';
import primeVesselOverlayAstralReliquaryImg from '../../assets/enemy_prime_vessel_4.png';
import enemyUI from '../assets/enemy_UI.png';
import combatUiOverlayPng from "../assets/combat_UI_overlay.png";
import recastPng from '../assets/combat_UI_recast.png';
import focusPng from '../assets/combat_UI_focus.png';
import combatBackgroundImg from "../assets/background.jpg";
import shadowVignettePng from "../assets/shadow_vignette.png";
import enemyHitVfxSheetPng from "../assets/vfx/hit/hit_vfx_sheet.png";
import preimpactSheetPng from "../assets/vfx/preimpact/preimpact.png";
import decreeBannerImg from '../../assets/ui/text_envelop.png';
import { ENEMY_HIT_VFX, PREIMPACT_VFX, TURN_HANDOFF_BANNER, getTurnHandoffBannerSpritesheetConfig } from '../config/vfx';
import { getEnemyDef } from '../game/enemies';
import { resolveInvokeAnimationVariantForPlay } from '../game/engine';
import type { InvokeAnimationVariant, TurnHandoffBannerKind, TurnHandoffBannerMode } from '../config/vfx';

type FloatingFeedbackAnchor =
  | 'playerHp'
  | 'playerSp'
  | 'playerArmor'
  | 'enemyHp'
  | 'enemyArmor';

type FloatingFeedbackTone =
  | 'damage'
  | 'gain'
  | 'armorLoss'
  | 'armorGain'
  | 'info';

type FloatingFeedbackItem = {
  id: string;
  anchor: FloatingFeedbackAnchor;
  text: string;
  tone: FloatingFeedbackTone;
};

type ImpactFeedbackItem = {
  id: string;
  text: string;
  label?: string;
  x: number;
  y: number;
};

type ImpactViewportRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type EnemyImpactVfxState = {
  tick: number;
  frame: number;
  x: number;
  y: number;
};

type EnemyPreImpactVfxState = {
  tick: number;
  frame: number;
  durationMs?: number;
};

type LungeImpactEventDetail = {
  cardId?: string;
  cardTitle?: string;
  clientX?: number;
  clientY?: number;
};

type LungePreImpactEventDetail = {
  cardId?: string;
  cardTitle?: string;
  durationMs?: number;
};

type DelayedDamageSourceEventDetail = {
  sourceTitle?: string;
  clientX?: number;
  clientY?: number;
};

type PlayerCardDamageSourceEventDetail = {
  cardId?: string;
  cardTitle?: string;
  clientX?: number;
  clientY?: number;
};

type PositionedFloatingFeedbackItem = FloatingFeedbackItem & {
  offsetX: number;
};

function positionFloatingFeedbackItems(items: FloatingFeedbackItem[]): PositionedFloatingFeedbackItem[] {
  return items.map((item) => {
    const siblings = items.filter((candidate) => candidate.anchor === item.anchor);
    const index = siblings.findIndex((candidate) => candidate.id === item.id);
    const gap = item.anchor === 'enemyHp' || item.anchor === 'enemyArmor' ? 34 : 42;
    const offsetX = (index - (siblings.length - 1) / 2) * gap;

    return {
      ...item,
      offsetX,
    };
  });
}

type RelicFeedbackBadge = {
  text: string;
  tick: number;
};

type PlayCommitArgs = {
  addendumPay?: number;
  xPaySp?: number;
  addendumResource?: 'sp' | 'hp';
  xPayHp?: number;
};

type HandPlayRequestResult =
  | { kind: 'immediate'; args?: PlayCommitArgs; invokeVariant?: InvokeAnimationVariant | null }
  | { kind: 'deferred'; invokeVariant?: InvokeAnimationVariant | null }
  | { kind: 'blocked' };

type ActiveTurnHandoffBanner = {
  key: string;
  kind: TurnHandoffBannerKind;
  mode: TurnHandoffBannerMode;
};

const DEFERRED_PLAY_EVENT = 'combat:deferred-play-confirmed';
const LUNGE_PREIMPACT_EVENT = 'combat:lunge-preimpact';
const LUNGE_IMPACT_EVENT = 'combat:lunge-impact';
const DELAYED_DAMAGE_SOURCE_EVENT = 'combat:delayed-damage-source';
const PLAYER_CARD_DAMAGE_SOURCE_EVENT = 'combat:player-card-damage-source';
const PLAYER_VISUAL_BUSY_EVENT = 'combat:player-visual-busy';
const PLAYER_VISUAL_IDLE_EVENT = 'combat:player-visual-idle';
const RECAST_SELECTION_ANIMATE_EVENT = 'combat:recast-selection-animate';
const RECAST_SELECTION_DISCARD_DONE_EVENT = 'combat:recast-selection-discard-done';
const DECREE_BANNER_DURATION_MS = 9000;
const DECREE_BANNER_TEXT_MAX_FONT_PX_DESKTOP = 24;
const DECREE_BANNER_TEXT_MAX_FONT_PX_MOBILE = 20;
const DECREE_BANNER_TEXT_MIN_FONT_PX = 10;

function dispatchRecastSelectionAnimation(requestId: string, cardIds: string[], drawCount: number) {
  if (typeof window === 'undefined') return;
  const fire = () => {
    window.dispatchEvent(new CustomEvent(RECAST_SELECTION_ANIMATE_EVENT, {
      detail: { requestId, cardIds, drawCount },
    }));
  };

  if (typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(fire);
    return;
  }

  window.setTimeout(fire, 0);
}

function isAttackCard(card: any): boolean {
  const effects = Array.isArray(card?.effects) ? card.effects : [];
  return effects.some((effect: any) => effect?.type === 'damage' || effect?.type === 'damageIgnoreArmor');
}

function dispatchDeferredPlay(cardId: string, args?: PlayCommitArgs, alreadyCommitted = false, invokeVariant?: InvokeAnimationVariant | null) {
  if (typeof window === 'undefined') return;
  const fire = () => {
    window.dispatchEvent(new CustomEvent(DEFERRED_PLAY_EVENT, { detail: { cardId, args, alreadyCommitted, invokeVariant } }));
  };

  if (typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(fire);
    return;
  }

  window.setTimeout(fire, 0);
}


function getPrependedLogEntries(prevLog: string[] | undefined, nextLog: string[] | undefined): string[] {
  const prev = Array.isArray(prevLog) ? prevLog : [];
  const next = Array.isArray(nextLog) ? nextLog : [];
  if (prev.length === 0) return next.slice(0, 12);
  if (next.length === 0) return [];

  const prevHead = prev[0];
  const directIndex = prevHead ? next.indexOf(prevHead) : -1;
  if (directIndex > 0) return next.slice(0, directIndex);
  if (directIndex === 0) return [];

  const probe = prev.slice(0, Math.min(3, prev.length));
  for (let i = 0; i < next.length; i += 1) {
    let matches = true;
    for (let j = 0; j < probe.length; j += 1) {
      if (next[i + j] !== probe[j]) {
        matches = false;
        break;
      }
    }
    if (matches) return next.slice(0, i);
  }

  return next.slice(0, Math.min(next.length, 12));
}

function parseLatestDealtDamageFromLogs(entries: string[], actorName?: string): number | null {
  const escapedActor = String(actorName ?? '').trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const actorPattern = escapedActor ? `${escapedActor}\\s+` : '(?:.+?)\\s+';
  const rx = new RegExp(`^${actorPattern}deals\\s+(\\d+)(?:\\s*\\(x\\d+\\))?\\s*\\((\\d+)\\s+to\\s+HP\\)`, 'i');
  for (const entry of entries) {
    const match = entry.match(rx);
    if (match) return Math.max(0, Number(match[1]) || 0);
  }
  return null;
}

function parseLatestArmorGainFromLogs(entries: string[], actorName?: string): number | null {
  const escapedActor = String(actorName ?? '').trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const actorPattern = escapedActor ? `${escapedActor}\\s+` : '(?:.+?)\\s+';
  const rx = new RegExp(`^${actorPattern}gains\\s+(\\d+)\\s+armor\\.?$`, 'i');
  for (const entry of entries) {
    const match = entry.match(rx);
    if (match) return Math.max(0, Number(match[1]) || 0);
  }
  return null;
}

type Props = {
  state: GameState;
  onPlay: (cardId: string, addendumPay?: number, xPaySp?: number, addendumResource?: 'sp' | 'hp', xPayHp?: number) => void;
  onPrepareDiscardPrereqPlay?: (cardId: string, addendumPay?: number, xPaySp?: number, addendumResource?: 'sp' | 'hp', xPayHp?: number) => void;
  onDeckBuilder: () => void;
  onReload: () => void;
  onAcknowledgeEnemy: () => void;
  onConfirmPeek: (orderedIds: string[]) => void;
  onConfirmChoice: (optionIndex: number) => void;
  onConfirmX: (value: number) => void;
  onConfirmTutor: (cardId: string) => void;
  onConfirmTopPick: (cardId: string) => void;
  onSkipTurn: () => void;
  onReshuffleSelected: (cardIds: string[]) => void;
  onExitToDungeon?: () => void;
  exitLabel?: string;
  /**
   * An optional list of relics (trinkets) owned by the player. When provided,
   * the board will render a RelicBar overlay showing these items at the
   * bottom of the screen. Each relic should have at least id, name,
   * description and image fields; extra fields are ignored.
   */
  maxHp?: number;
  maxSp?: number;
  relics?: any[];
  torchProgress?: number;
  lightRadius?: number;
  enemyPortraitVariant?: string;
  lowFx?: boolean;
  aggressiveLowFx?: boolean;
  suppressInitialTurnHandoff?: boolean;
};

export default function Board({
  state,
  onPlay,
  onPrepareDiscardPrereqPlay,
  onDeckBuilder,
  onReload,
  onAcknowledgeEnemy,
  onConfirmPeek,
  onConfirmChoice,
  onConfirmX,
  onConfirmTutor,
  onConfirmTopPick,
  onSkipTurn,
  onReshuffleSelected,
  onExitToDungeon,
  exitLabel,
  maxHp,
  maxSp,
  relics,
  torchProgress = 1,
  lightRadius,
  enemyPortraitVariant,
  lowFx: lowFxProp,
  aggressiveLowFx = false,
  suppressInitialTurnHandoff = false,
}: Props) {
  // Prefer live caps from combat state (supports temporary in-combat Max HP changes like Endure),
  // but fall back to props (dungeon layer) and finally 100.
  const baseMaxHp = Math.floor(Number((state as any)?.relicMods?.capHp ?? maxHp ?? 100) || 100);
  const baseMaxSp = Math.floor(Number((state as any)?.relicMods?.capSp ?? maxSp ?? 100) || 100);
  const tempMaxHpBonus = Math.max(0, Math.floor(Number((state as any)?.tempMaxHpBonus ?? 0) || 0));
  const liveMaxHp = baseMaxHp + tempMaxHpBonus;
  const liveMaxSp = baseMaxSp;

  const combatTorchProgress = Math.max(0, Math.min(1, Number.isFinite(Number(torchProgress)) ? Number(torchProgress) : 1));
  const combatLightRadius = Math.max(1, Number.isFinite(Number(lightRadius)) ? Number(lightRadius) : Math.max(1, Math.ceil(combatTorchProgress * 4)));
  const combatSanityPct = liveMaxSp > 0 ? Math.max(0, Math.min(1, state.player.sp / liveMaxSp)) : 1;
  const combatMadness = 1 - combatSanityPct;
  const combatSanityTier =
    combatSanityPct <= 0.25 ? 3 :
    combatSanityPct <= 0.50 ? 2 :
    combatSanityPct <= 0.75 ? 1 :
    0;
  const combatTier1Progress = combatSanityTier >= 1 ? Math.min(1, Math.max(0, (combatMadness - 0.25) / 0.75)) : 0;
  const combatTier2Progress = combatSanityTier >= 2 ? Math.min(1, Math.max(0, (combatMadness - 0.50) / 0.50)) : 0;
  const combatTier3Progress = combatSanityTier >= 3 ? Math.min(1, Math.max(0, (combatMadness - 0.75) / 0.25)) : 0;
  const combatVignetteOpacity = combatSanityTier >= 1 ? 0.08 + combatTier1Progress * 0.28 : 0;
  const combatVignettePulseOpacity = combatSanityTier >= 1 ? 0.05 + combatTier1Progress * 0.18 : 0;
  const combatTorchCorruption = combatSanityTier >= 2 ? 0.12 + combatTier2Progress * 0.68 : 0;

  const isPlayerTurn = state.phase === 'player';
  const choiceMode = !!state.pendingChoice;
  const xMode = isPlayerTurn && !!state.pendingX;
  const tutorMode = isPlayerTurn && !!state.pendingTutor;
  const topPickMode = isPlayerTurn && !!(state as any).pendingTopPick;
    const peekMode = isPlayerTurn && !!state.pendingPeek;
const discardMode = !!state.pendingDiscard && state.pendingDiscard.remaining > 0;

  const isNestedHorror = state.enemyId === 'nested_horror';
  const isHexbinder = state.enemyId === 'hexbinder';
  const isDreadbrand = state.enemyId === 'dreadbrand';
  const isCorruptedCultist = state.enemyId === 'corrupted_cultist';
  const isChainedPenitent = state.enemyId === 'chained-penitent';
  const isMonsignorGuardPortrait =
    isChainedPenitent && enemyPortraitVariant === 'monsignor-guard';
  const isMonsignor = state.enemyId === 'monsignor';
  const isDarkApostle = state.enemyId === 'dark-apostle';
  const isChymist = state.enemyId === 'chymist';
  const isMercenary = state.enemyId === 'mercenary';
  const isLampKeeper = state.enemyId === 'lamp_keeper';
  const isThresholdVerifier = state.enemyId === 'threshold_verifier';
  const isCustodian = state.enemyId === 'custodian';
  const isBlackhand = state.enemyId === 'blackhand';
  const isPrimeVessel = state.enemyId === 'prime_vessel';
  const isPrimeCrucibleDevice = state.enemyId === 'prime_crucible_device';
  const isPulseEngineDevice = state.enemyId === 'pulse_engine_device';
  const isSynapticRetortDevice = state.enemyId === 'synaptic_retort_device';
  const isAstralReliquaryDevice = state.enemyId === 'astral_reliquary_device';
  const isYahaka = typeof state.enemyId === 'string' && state.enemyId.startsWith('yahaka_voidfeeder');
  const yahakaVariantMatch = String(state.enemyId ?? '').match(/_(\d+)$/);
  const yahakaVariant = Math.max(1, Math.floor(Number(yahakaVariantMatch?.[1] || 1)));
  const enemyName = getEnemyDef(state.enemyId).name;
  const scenePortrait = isNestedHorror
  ? nestedHorrorPortrait
  : isHexbinder
    ? hexbinderPortrait
    : isDreadbrand
      ? dreadbrandPortrait
      : isCorruptedCultist
        ? corruptedCultistPortrait
        : isChainedPenitent
        ? (isMonsignorGuardPortrait ? chainedPenitentPortrait2 : chainedPenitentPortrait)
        : isMonsignor
          ? monsignorPortrait
          : isDarkApostle
            ? darkApostlePortrait
            : isChymist
              ? chymistPortrait
              : isMercenary
                ? mercenaryPortrait
                : isLampKeeper
                  ? lampKeeperPortrait
                  : isThresholdVerifier
                    ? thresholdVerifierPortrait
                    : isCustodian
                      ? custodianPortrait
                      : isBlackhand
              ? blackhandPortrait
              : isPrimeVessel
                ? primeVesselPortrait
                : isPrimeCrucibleDevice
                  ? primeCrucibleDevicePortrait
                  : isPulseEngineDevice
                    ? pulseEngineDevicePortrait
                    : isSynapticRetortDevice
                      ? synapticRetortDevicePortrait
                      : isAstralReliquaryDevice
                        ? astralReliquaryDevicePortrait
                        : isYahaka
                          ? (yahakaVariant >= 7
                              ? yahakaPortrait4
                              : yahakaVariant >= 5
                                ? yahakaPortrait3
                                : yahakaVariant >= 3
                                  ? yahakaPortrait2
                                  : yahakaPortrait)
                          : cultistPortrait;

  const primeVesselDestroyedPortraitOverlays = React.useMemo(() => {
    if (!isPrimeVessel) return [] as string[];

    const relicMods = ((state as any)?.relicMods ?? {}) as any;
    const overlays: string[] = [];

    if (!relicMods.primeVesselPrimeCrucibleActive) {
      overlays.push(primeVesselOverlayPrimeCrucibleImg);
    }
    if (!relicMods.primeVesselSynapticRetortActive) {
      overlays.push(primeVesselOverlaySynapticRetortImg);
    }
    if (!relicMods.primeVesselPulseEngineActive) {
      overlays.push(primeVesselOverlayPulseEngineImg);
    }
    if (!relicMods.primeVesselAstralReliquaryActive) {
      overlays.push(primeVesselOverlayAstralReliquaryImg);
    }

    return overlays;
  }, [isPrimeVessel, (state as any)?.relicMods]);

  const combatOverlayMaskStyle = React.useMemo(() => ({
    WebkitMaskImage: `url(${combatUiOverlayPng})`,
    WebkitMaskRepeat: 'no-repeat',
    WebkitMaskSize: '100% 100%',
    WebkitMaskPosition: 'bottom center',
    maskImage: `url(${combatUiOverlayPng})`,
    maskRepeat: 'no-repeat',
    maskSize: '100% 100%',
    maskPosition: 'bottom center',
  }) as React.CSSProperties, []);


  const prevStateRef = React.useRef<GameState | null>(null);
  const floatingSeqRef = React.useRef(0);
  const pulseSeqRef = React.useRef(0);
  const playerVisualTokensRef = React.useRef<Set<string>>(new Set());
  const floatingFeedbackActiveCountRef = React.useRef(0);
  const enemyStartReleaseInFlightRef = React.useRef(false);
  const combatEndReleaseInFlightRef = React.useRef(false);
  const autoFocusReleaseInFlightRef = React.useRef(false);
  const sceneRef = React.useRef<HTMLDivElement | null>(null);
  const bottomRef = React.useRef<HTMLDivElement | null>(null);
  const impactViewportRectRef = React.useRef<ImpactViewportRect | null>(null);
  const [impactViewportRect, setImpactViewportRect] = React.useState<ImpactViewportRect | null>(null);
  const recentLungeImpactRef = React.useRef<null | { at: number; cardId?: string; cardTitle?: string; clientX: number; clientY: number }>(null);
  const recentPlayerCardDamageSourceRef = React.useRef<null | { at: number; cardId?: string; cardTitle?: string; clientX: number; clientY: number }>(null);
  const delayedDamageAnchorByTitleRef = React.useRef<Record<string, { clientX: number; clientY: number; at: number }>>({});

const relicFeedbackSeqRef = React.useRef(0);
const relicFeedbackTimeoutsRef = React.useRef<Record<string, Record<number, number>>>({});

  const [floatingFeedbackItems, setFloatingFeedbackItems] = React.useState<FloatingFeedbackItem[]>([]);
  const [impactFeedbackItems, setImpactFeedbackItems] = React.useState<ImpactFeedbackItem[]>([]);
  const [playerDamageFlashTick, setPlayerDamageFlashTick] = React.useState(0);
  const [playerHealFlashTick, setPlayerHealFlashTick] = React.useState(0);
  const [playerSpFlashTick, setPlayerSpFlashTick] = React.useState(0);
  const [combatSanityScreenFlicker, setCombatSanityScreenFlicker] = React.useState<any>(null);
  const [combatSanityPeripheralShadow, setCombatSanityPeripheralShadow] = React.useState<any>(null);
  const combatSanityScreenFlickerTimeoutRef = React.useRef<number | null>(null);
  const combatSanityScreenFlickerLoopTimeoutRef = React.useRef<number | null>(null);
  const combatSanityPeripheralShadowTimeoutRef = React.useRef<number | null>(null);
  const combatSanityPeripheralShadowLoopTimeoutRef = React.useRef<number | null>(null);
  const [enemyHitTick, setEnemyHitTick] = React.useState(0);
  const [enemyImpactVfxState, setEnemyImpactVfxState] = React.useState<EnemyImpactVfxState | null>(null);
  const [enemyPreImpactVfxState, setEnemyPreImpactVfxState] = React.useState<EnemyPreImpactVfxState | null>(null);
  const [enemyPortraitImpactFlashTick, setEnemyPortraitImpactFlashTick] = React.useState(0);
  const enemyImpactVfxTimeoutsRef = React.useRef<number[]>([]);
  const enemyPreImpactVfxTimeoutsRef = React.useRef<number[]>([]);
  const pendingAttackImpactShakeRef = React.useRef(false);
  const [playerSpPulseTick, setPlayerSpPulseTick] = React.useState(0);
  const [relicPulseMap, setRelicPulseMap] = React.useState<Record<string, number>>({});
  const [statusPulseMap, setStatusPulseMap] = React.useState<Partial<Record<CombatUiStatusPulseKey, number>>>({});

  const [relicFeedbackMap, setRelicFeedbackMap] = React.useState<Record<string, RelicFeedbackBadge[]>>({});
  const [playerVisualGateTick, setPlayerVisualGateTick] = React.useState(0);
  const [activeDecreeBanner, setActiveDecreeBanner] = React.useState<{ key: string; text: string } | null>(null);
  const [decreeBannerTextFontPx, setDecreeBannerTextFontPx] = React.useState<number | null>(null);
  const decreeBannerTimerRef = React.useRef<number | null>(null);
  const lastSeenDecreeKeyRef = React.useRef<string>('');
  const decreeBannerTextWrapRef = React.useRef<HTMLDivElement | null>(null);
  const decreeBannerTextRef = React.useRef<HTMLDivElement | null>(null);

  const [activeTurnHandoffBanner, setActiveTurnHandoffBanner] = React.useState<ActiveTurnHandoffBanner | null>(null);
  const [turnHandoffSpriteFrame, setTurnHandoffSpriteFrame] = React.useState(0);
  const turnHandoffTimerRef = React.useRef<number | null>(null);
  const turnHandoffAnimationFrameRef = React.useRef<number | null>(null);
  const lastShownEnemyTurnHandoffKeyRef = React.useRef<string>('');
  const lastShownPlayerTurnHandoffKeyRef = React.useRef<string>('');

  // Mobile composition: dynamically reserve equal side gutters
  // and scale the enemy HUD/chips so they fit without overlapping the portrait.
  const [mobileSideGutter, setMobileSideGutter] = React.useState<number | null>(null);
  const autoLowFx = mobileSideGutter !== null || (typeof window !== 'undefined' && (window.matchMedia ? window.matchMedia('(max-width: 768px)').matches : window.innerWidth <= 768));
  const lowFx = typeof lowFxProp === 'boolean' ? lowFxProp : autoLowFx;
  const [enemyHudScale, setEnemyHudScale] = React.useState<number>(1);
  const [enemyChipScale, setEnemyChipScale] = React.useState<number>(1);

  React.useEffect(() => {
    return () => {
      if (combatSanityScreenFlickerTimeoutRef.current) window.clearTimeout(combatSanityScreenFlickerTimeoutRef.current);
      if (combatSanityScreenFlickerLoopTimeoutRef.current) window.clearTimeout(combatSanityScreenFlickerLoopTimeoutRef.current);
      if (combatSanityPeripheralShadowTimeoutRef.current) window.clearTimeout(combatSanityPeripheralShadowTimeoutRef.current);
      if (combatSanityPeripheralShadowLoopTimeoutRef.current) window.clearTimeout(combatSanityPeripheralShadowLoopTimeoutRef.current);
      enemyImpactVfxTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      enemyImpactVfxTimeoutsRef.current = [];
      enemyPreImpactVfxTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      enemyPreImpactVfxTimeoutsRef.current = [];
      if (decreeBannerTimerRef.current) {
        window.clearTimeout(decreeBannerTimerRef.current);
        decreeBannerTimerRef.current = null;
      }
      if (turnHandoffAnimationFrameRef.current) {
        window.cancelAnimationFrame(turnHandoffAnimationFrameRef.current);
        turnHandoffAnimationFrameRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    if (isMonsignor && state.phase !== 'loading') return;

    if (decreeBannerTimerRef.current) {
      window.clearTimeout(decreeBannerTimerRef.current);
      decreeBannerTimerRef.current = null;
    }

    lastSeenDecreeKeyRef.current = '';
    setActiveDecreeBanner(null);
    setDecreeBannerTextFontPx(null);
  }, [isMonsignor, state.phase, state.enemyId]);

  const currentMonsignorDecree = (state as any)?.monsignorDecree;
  const currentMonsignorDecreeText = typeof currentMonsignorDecree?.text === 'string'
    ? currentMonsignorDecree.text.trim()
    : '';
  const currentMonsignorDecreeId = typeof currentMonsignorDecree?.id === 'string'
    ? currentMonsignorDecree.id.trim()
    : '';
  const currentMonsignorDecreeBroken = !!currentMonsignorDecree?.broken;
  const currentMonsignorDecreeKey = currentMonsignorDecreeId && currentMonsignorDecreeText
    ? `${currentMonsignorDecreeId}::${currentMonsignorDecreeText}`
    : '';
  const currentMonsignorDecreeBannerKey = currentMonsignorDecreeKey
    ? `${currentMonsignorDecreeKey}::${currentMonsignorDecreeBroken ? 'broken' : 'active'}`
    : '';
  const currentMonsignorDecreeBannerText = currentMonsignorDecreeBroken
    ? 'Decree broken'
    : currentMonsignorDecreeText;

  React.useEffect(() => {
    if (!isMonsignor || state.phase === 'loading' || !currentMonsignorDecreeBannerKey) {
      if (decreeBannerTimerRef.current) {
        window.clearTimeout(decreeBannerTimerRef.current);
        decreeBannerTimerRef.current = null;
      }
      lastSeenDecreeKeyRef.current = '';
      setActiveDecreeBanner(null);
      setDecreeBannerTextFontPx(null);
      return;
    }

    if (currentMonsignorDecreeBannerKey === lastSeenDecreeKeyRef.current) return;
    lastSeenDecreeKeyRef.current = currentMonsignorDecreeBannerKey;

    setActiveDecreeBanner({ key: currentMonsignorDecreeBannerKey, text: currentMonsignorDecreeBannerText });

    if (decreeBannerTimerRef.current) {
      window.clearTimeout(decreeBannerTimerRef.current);
    }

    decreeBannerTimerRef.current = window.setTimeout(() => {
      setActiveDecreeBanner((current) => (current?.key === currentMonsignorDecreeBannerKey ? null : current));
      decreeBannerTimerRef.current = null;
    }, DECREE_BANNER_DURATION_MS);
  }, [
    isMonsignor,
    state.phase,
    state.enemyId,
    currentMonsignorDecreeBannerKey,
    currentMonsignorDecreeBannerText,
  ]);

  React.useLayoutEffect(() => {
    if (!activeDecreeBanner || !isMonsignor) return;
    if (typeof window === 'undefined') return;

    let raf = 0;
    let ro: ResizeObserver | null = null;

    const fitDecreeText = () => {
      const wrap = decreeBannerTextWrapRef.current;
      const text = decreeBannerTextRef.current;
      if (!wrap || !text) return;

      const isMobileViewport = window.matchMedia
        ? window.matchMedia('(max-width: 768px)').matches
        : window.innerWidth <= 768;

      const maxFont = isMobileViewport
        ? DECREE_BANNER_TEXT_MAX_FONT_PX_MOBILE
        : DECREE_BANNER_TEXT_MAX_FONT_PX_DESKTOP;

      let fontPx = maxFont;
      text.style.fontSize = `${fontPx}px`;
      text.style.whiteSpace = 'nowrap';

      const availableWidth = Math.max(0, Math.floor(wrap.clientWidth));
      if (!availableWidth) return;

      while (fontPx > DECREE_BANNER_TEXT_MIN_FONT_PX && text.scrollWidth > availableWidth) {
        fontPx -= 0.5;
        text.style.fontSize = `${fontPx}px`;
      }

      const rounded = Math.round(fontPx * 10) / 10;
      setDecreeBannerTextFontPx((prev) => (prev === rounded ? prev : rounded));
    };

    const scheduleFit = () => {
      if (raf) window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        fitDecreeText();
      });
    };

    scheduleFit();
    window.addEventListener('resize', scheduleFit);

    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(scheduleFit);
      if (decreeBannerTextWrapRef.current) ro.observe(decreeBannerTextWrapRef.current);
      if (decreeBannerTextRef.current) ro.observe(decreeBannerTextRef.current);
    }

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener('resize', scheduleFit);
    };
  }, [activeDecreeBanner, isMonsignor]);

  const triggerCombatSanityScreenFlicker = React.useCallback(() => {
    if (combatSanityTier <= 0) return;

    const durationMs = 120 + Math.floor(Math.random() * 180);
    const next = {
      id: `${Date.now()}-${Math.random()}`,
      durationMs,
      darknessAlpha: 0.04 + combatMadness * 0.10,
      violetAlpha: 0.05 + combatMadness * 0.16,
      blurPx: (0.6 + combatMadness * 1.8).toFixed(2),
      scale: (1 + combatMadness * 0.012).toFixed(3),
    };

    setCombatSanityScreenFlicker(next);

    if (combatSanityScreenFlickerTimeoutRef.current) {
      window.clearTimeout(combatSanityScreenFlickerTimeoutRef.current);
    }

    combatSanityScreenFlickerTimeoutRef.current = window.setTimeout(() => {
      setCombatSanityScreenFlicker(null);
      combatSanityScreenFlickerTimeoutRef.current = null;
    }, durationMs + 60);
  }, [combatMadness, combatSanityTier]);

  const triggerCombatSanityPeripheralShadow = React.useCallback(() => {
    if (combatSanityTier < 3) return;

    const sidePool = ['left', 'right', 'top', 'bottom'];
    const side = sidePool[Math.floor(Math.random() * sidePool.length)];
    const durationMs = 380 + Math.floor(Math.random() * 420);

    const next = {
      id: `${Date.now()}-${Math.random()}`,
      side,
      durationMs,
      opacity: 0.10 + combatMadness * 0.18,
      blurPx: (10 + combatMadness * 18).toFixed(2),
      driftPx: (20 + combatMadness * 40).toFixed(2),
      widthPct: (side === 'left' || side === 'right') ? 22 + Math.random() * 12 : 46 + Math.random() * 18,
      heightPct: (side === 'top' || side === 'bottom') ? 20 + Math.random() * 12 : 48 + Math.random() * 16,
    };

    setCombatSanityPeripheralShadow(next);

    if (combatSanityPeripheralShadowTimeoutRef.current) {
      window.clearTimeout(combatSanityPeripheralShadowTimeoutRef.current);
    }

    combatSanityPeripheralShadowTimeoutRef.current = window.setTimeout(() => {
      setCombatSanityPeripheralShadow(null);
      combatSanityPeripheralShadowTimeoutRef.current = null;
    }, durationMs + 80);
  }, [combatMadness, combatSanityTier]);

  React.useEffect(() => {
    if (lowFx || combatSanityTier <= 0) {
      setCombatSanityScreenFlicker(null);
      if (combatSanityScreenFlickerTimeoutRef.current) {
        window.clearTimeout(combatSanityScreenFlickerTimeoutRef.current);
        combatSanityScreenFlickerTimeoutRef.current = null;
      }
      if (combatSanityScreenFlickerLoopTimeoutRef.current) {
        window.clearTimeout(combatSanityScreenFlickerLoopTimeoutRef.current);
        combatSanityScreenFlickerLoopTimeoutRef.current = null;
      }
      return;
    }

    let cancelled = false;

    const schedule = () => {
      const baseDelay =
        combatSanityTier === 1 ? 18000 :
        combatSanityTier === 2 ? 11000 :
        7500;

      const jitter = 1200 + Math.random() * 2800;

      combatSanityScreenFlickerLoopTimeoutRef.current = window.setTimeout(() => {
        if (cancelled) return;
        triggerCombatSanityScreenFlicker();
        schedule();
      }, baseDelay + jitter);
    };

    schedule();

    return () => {
      cancelled = true;
      if (combatSanityScreenFlickerLoopTimeoutRef.current) {
        window.clearTimeout(combatSanityScreenFlickerLoopTimeoutRef.current);
        combatSanityScreenFlickerLoopTimeoutRef.current = null;
      }
    };
  }, [combatSanityTier, triggerCombatSanityScreenFlicker, lowFx]);

  React.useEffect(() => {
    if (lowFx || combatSanityTier < 3) {
      setCombatSanityPeripheralShadow(null);
      if (combatSanityPeripheralShadowTimeoutRef.current) {
        window.clearTimeout(combatSanityPeripheralShadowTimeoutRef.current);
        combatSanityPeripheralShadowTimeoutRef.current = null;
      }
      if (combatSanityPeripheralShadowLoopTimeoutRef.current) {
        window.clearTimeout(combatSanityPeripheralShadowLoopTimeoutRef.current);
        combatSanityPeripheralShadowLoopTimeoutRef.current = null;
      }
      return;
    }

    let cancelled = false;

    const schedule = () => {
      const baseDelay = 9500;
      const jitter = 2500 + Math.random() * 5000;

      combatSanityPeripheralShadowLoopTimeoutRef.current = window.setTimeout(() => {
        if (cancelled) return;
        triggerCombatSanityPeripheralShadow();
        schedule();
      }, baseDelay + jitter);
    };

    schedule();

    return () => {
      cancelled = true;
      if (combatSanityPeripheralShadowLoopTimeoutRef.current) {
        window.clearTimeout(combatSanityPeripheralShadowLoopTimeoutRef.current);
        combatSanityPeripheralShadowLoopTimeoutRef.current = null;
      }
    };
  }, [combatSanityTier, triggerCombatSanityPeripheralShadow, lowFx]);

  const playerFloatingFeedbackItems = React.useMemo(
    () => positionFloatingFeedbackItems(
      floatingFeedbackItems.filter((item) => item.anchor === 'playerHp' || item.anchor === 'playerSp' || item.anchor === 'playerArmor')
    ),
    [floatingFeedbackItems]
  );

  const enemyFloatingFeedbackItems = React.useMemo(
    () => positionFloatingFeedbackItems(
      floatingFeedbackItems.filter((item) => item.anchor === 'enemyHp' || item.anchor === 'enemyArmor')
    ),
    [floatingFeedbackItems]
  );

  const addFloatingFeedback = React.useCallback(
    (anchor: FloatingFeedbackAnchor, text: string, tone: FloatingFeedbackTone) => {
      floatingSeqRef.current += 1;
      const id = `float-${floatingSeqRef.current}`;

      floatingFeedbackActiveCountRef.current += 1;
      setPlayerVisualGateTick((tick) => tick + 1);
      setFloatingFeedbackItems((prev) => [...prev, { id, anchor, text, tone }]);

      window.setTimeout(() => {
        floatingFeedbackActiveCountRef.current = Math.max(0, floatingFeedbackActiveCountRef.current - 1);
        setPlayerVisualGateTick((tick) => tick + 1);
        setFloatingFeedbackItems((prev) => prev.filter((item) => item.id !== id));
      }, 1150);
    },
    []
  );

  const triggerEnemyImpactBeat = React.useCallback((clientX: number, clientY: number, startFrame = 0) => {
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return;

    const viewportRect = impactViewportRectRef.current;
    const localX = viewportRect ? clientX - viewportRect.left : clientX;
    const localY = viewportRect ? clientY - viewportRect.top : clientY;

    pulseSeqRef.current += 1;
    const impactTick = pulseSeqRef.current;
    const clampedStartFrame = Math.max(0, Math.min(ENEMY_HIT_VFX.frameCount - 1, startFrame));

    setEnemyPreImpactVfxState(null);
    setEnemyHitTick(impactTick);
    setEnemyPortraitImpactFlashTick(impactTick);
    setEnemyImpactVfxState({ tick: impactTick, frame: clampedStartFrame, x: localX, y: localY });

    return { localX, localY };
  }, []);

  const addImpactFeedback = React.useCallback((clientX: number, clientY: number, text: string, label?: string, options?: { triggerImpactBeat?: boolean }) => {
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return;

    const triggerImpactBeat = options?.triggerImpactBeat ?? true;
    const beatPosition = triggerImpactBeat
      ? triggerEnemyImpactBeat(clientX, clientY, ENEMY_HIT_VFX.startFrameAtDamageNumber)
      : (() => {
          const viewportRect = impactViewportRectRef.current;
          return {
            localX: viewportRect ? clientX - viewportRect.left : clientX,
            localY: viewportRect ? clientY - viewportRect.top : clientY,
          };
        })();

    const localX = beatPosition?.localX ?? clientX;
    const localY = beatPosition?.localY ?? clientY;

    floatingSeqRef.current += 1;
    const id = `impact-${floatingSeqRef.current}`;

    floatingFeedbackActiveCountRef.current += 1;
    setPlayerVisualGateTick((tick) => tick + 1);

    setImpactFeedbackItems((prev) => [...prev, {
      id,
      text,
      label,
      x: localX,
      y: localY,
    }]);

    window.setTimeout(() => {
      floatingFeedbackActiveCountRef.current = Math.max(0, floatingFeedbackActiveCountRef.current - 1);
      setPlayerVisualGateTick((tick) => tick + 1);
      setImpactFeedbackItems((prev) => prev.filter((item) => item.id !== id));
    }, ENEMY_HIT_VFX.feedbackLifetimeMs);
  }, [triggerEnemyImpactBeat]);

  const pulseRelic = React.useCallback((relicId: string) => {
    pulseSeqRef.current += 1;
    setRelicPulseMap((prev) => ({ ...prev, [relicId]: pulseSeqRef.current }));
  }, []);

  const pulseStatus = React.useCallback((key: CombatUiStatusPulseKey) => {
    pulseSeqRef.current += 1;
    setStatusPulseMap((prev) => ({ ...prev, [key]: pulseSeqRef.current }));
  }, []);

const showRelicFeedback = React.useCallback((relicId: string, text: string) => {
  if (!text) return;

  relicFeedbackSeqRef.current += 1;
  const tick = relicFeedbackSeqRef.current;
  const badge: RelicFeedbackBadge = { text, tick };

  setRelicFeedbackMap((prev) => {
    const current = Array.isArray(prev[relicId]) ? prev[relicId] : [];
    return {
      ...prev,
      [relicId]: [...current, badge].slice(-4),
    };
  });

  if (!relicFeedbackTimeoutsRef.current[relicId]) {
    relicFeedbackTimeoutsRef.current[relicId] = {};
  }

  relicFeedbackTimeoutsRef.current[relicId][tick] = window.setTimeout(() => {
    setRelicFeedbackMap((prev) => {
      const current = Array.isArray(prev[relicId]) ? prev[relicId] : [];
      const nextItems = current.filter((entry) => entry.tick !== tick);
      if (nextItems.length === current.length) return prev;
      const next = { ...prev };
      if (nextItems.length > 0) {
        next[relicId] = nextItems;
      } else {
        delete next[relicId];
      }
      return next;
    });

    const timeoutMap = relicFeedbackTimeoutsRef.current[relicId];
    if (timeoutMap) {
      delete timeoutMap[tick];
      if (Object.keys(timeoutMap).length === 0) {
        delete relicFeedbackTimeoutsRef.current[relicId];
      }
    }
  }, 3150);
}, []);

React.useEffect(() => {
  return () => {
    for (const timeoutMap of Object.values(relicFeedbackTimeoutsRef.current)) {
      for (const timeoutId of Object.values(timeoutMap)) {
        window.clearTimeout(timeoutId);
      }
    }
  };
}, []);

React.useEffect(() => {
  if (typeof window === 'undefined') return;

  const handleVisualBusy = (event: Event) => {
    const id = (event as CustomEvent<{ id?: string }>).detail?.id;
    if (!id || playerVisualTokensRef.current.has(id)) return;
    playerVisualTokensRef.current.add(id);
    setPlayerVisualGateTick((tick) => tick + 1);
  };

  const handleVisualIdle = (event: Event) => {
    const id = (event as CustomEvent<{ id?: string }>).detail?.id;
    if (!id || !playerVisualTokensRef.current.has(id)) return;
    playerVisualTokensRef.current.delete(id);
    setPlayerVisualGateTick((tick) => tick + 1);
  };

  window.addEventListener(PLAYER_VISUAL_BUSY_EVENT, handleVisualBusy as EventListener);
  window.addEventListener(PLAYER_VISUAL_IDLE_EVENT, handleVisualIdle as EventListener);
  return () => {
    window.removeEventListener(PLAYER_VISUAL_BUSY_EVENT, handleVisualBusy as EventListener);
    window.removeEventListener(PLAYER_VISUAL_IDLE_EVENT, handleVisualIdle as EventListener);
  };
}, []);

  React.useEffect(() => {
    if (!state.uiEvents || state.uiEvents.length === 0) return;

    for (const event of state.uiEvents) {
  if (event.kind === 'relicTriggered') {
    pulseRelic(event.relicId);
    if (event.text) {
      showRelicFeedback(event.relicId, event.text);
    }
    continue;
  }
  if (event.kind === 'statusPulse') {
    pulseStatus(event.key);
  }
}
  }, [state.uiEvents, pulseRelic, pulseStatus, showRelicFeedback]);

  React.useEffect(() => {
    const prev = prevStateRef.current;

    if (!prev) {
      prevStateRef.current = state;
      return;
    }

    const playerHpLoss = Math.max(0, prev.player.hp - state.player.hp);
    const playerHpGain = Math.max(0, state.player.hp - prev.player.hp);
    const playerSpLoss = Math.max(0, prev.player.sp - state.player.sp);
    const playerSpGain = Math.max(0, state.player.sp - prev.player.sp);
    const playerArmorLoss = Math.max(0, prev.player.armor - state.player.armor);
    const playerArmorGain = Math.max(0, state.player.armor - prev.player.armor);
    const enemyHpLoss = Math.max(0, prev.enemy.hp - state.enemy.hp);
    const enemyHpGain = Math.max(0, state.enemy.hp - prev.enemy.hp);
    const enemyArmorLoss = Math.max(0, prev.enemy.armor - state.enemy.armor);
    const enemyArmorGain = Math.max(0, state.enemy.armor - prev.enemy.armor);
    const prevDelayed = Array.isArray((prev as any)?.delayed) ? ((prev as any).delayed as any[]) : [];
    const nextDelayed = Array.isArray((state as any)?.delayed) ? ((state as any).delayed as any[]) : [];
    const nextDelayedIds = new Set(nextDelayed.map((bundle: any) => String(bundle?.id ?? '')));
    const resolvedDelayedBundles = prevDelayed.filter((bundle: any) => !nextDelayedIds.has(String(bundle?.id ?? '')));
    const prependedLogEntries = getPrependedLogEntries(prev.log, state.log);
    const delayedDamageFromLog = resolvedDelayedBundles.length > 0
      ? parseLatestDealtDamageFromLogs(prependedLogEntries, prev.player?.name ?? state.player?.name)
      : null;
    const enemyArmorGainFromLog = parseLatestArmorGainFromLogs(prependedLogEntries, prev.enemy?.name ?? state.enemy?.name);
    const shouldSplitDelayedArmorFeedback = (
      resolvedDelayedBundles.length > 0
      && enemyArmorGain > 0
      && enemyArmorLoss === 0
      && enemyHpLoss === 0
      && (delayedDamageFromLog ?? 0) > 0
      && (enemyArmorGainFromLog ?? 0) > enemyArmorGain
    );
    const visualEnemyArmorGain = shouldSplitDelayedArmorFeedback ? Math.max(enemyArmorGain, enemyArmorGainFromLog ?? enemyArmorGain) : enemyArmorGain;
    const visualEnemyArmorLoss = shouldSplitDelayedArmorFeedback ? Math.max(0, delayedDamageFromLog ?? 0) : enemyArmorLoss;
    const totalEnemyImpact = shouldSplitDelayedArmorFeedback
      ? Math.max(0, delayedDamageFromLog ?? 0)
      : (enemyHpLoss + enemyArmorLoss);

    if (playerHpLoss > 0) {
      addFloatingFeedback('playerHp', `-${playerHpLoss}`, 'damage');
      pulseSeqRef.current += 1;
      setPlayerDamageFlashTick(pulseSeqRef.current);
    }
    if (playerHpGain > 0) {
      addFloatingFeedback('playerHp', `+${playerHpGain}`, 'gain');
      pulseSeqRef.current += 1;
      setPlayerHealFlashTick(pulseSeqRef.current);
    }
    if (playerSpLoss > 0) addFloatingFeedback('playerSp', `-${playerSpLoss}`, 'damage');
    if (playerSpGain > 0) {
      addFloatingFeedback('playerSp', `+${playerSpGain}`, 'gain');
      pulseSeqRef.current += 1;
      setPlayerSpFlashTick(pulseSeqRef.current);
    }
    if (playerArmorLoss > 0) {
      addFloatingFeedback('playerArmor', `-${playerArmorLoss}`, 'armorLoss');
      if (playerHpLoss === 0) addFloatingFeedback('playerArmor', 'BLOCKED', 'info');
    }
    if (playerArmorGain > 0) addFloatingFeedback('playerArmor', `+${playerArmorGain}`, 'armorGain');
    if (enemyHpLoss > 0) addFloatingFeedback('enemyHp', `-${enemyHpLoss}`, 'damage');
    if (enemyHpGain > 0) addFloatingFeedback('enemyHp', `+${enemyHpGain}`, 'gain');
    if (visualEnemyArmorLoss > 0) {
      addFloatingFeedback('enemyArmor', `-${visualEnemyArmorLoss}`, 'armorLoss');
      if (enemyHpLoss === 0) addFloatingFeedback('enemyArmor', 'BLOCKED', 'info');
    }
    if (visualEnemyArmorGain > 0) addFloatingFeedback('enemyArmor', `+${visualEnemyArmorGain}`, 'armorGain');
    if (prev.player.negateNextDamage > state.player.negateNextDamage) {
      addFloatingFeedback('playerHp', 'NEGATED', 'info');
    }
    if (prev.player.reflectPct > state.player.reflectPct && (enemyHpLoss > 0 || visualEnemyArmorLoss > 0)) {
      addFloatingFeedback('enemyHp', 'REFLECT', 'info');
    }

    if (totalEnemyImpact > 0) {
      const now = typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now();
      const recentImpact = recentLungeImpactRef.current;
      const recentPlayerCardSource = recentPlayerCardDamageSourceRef.current;
      const immediateImpactActive = !!recentImpact && (now - recentImpact.at) <= 520;
      const recentPlayerCardImpactActive = !!recentPlayerCardSource && (now - recentPlayerCardSource.at) <= 3200;

      if (immediateImpactActive && recentImpact) {
        addImpactFeedback(recentImpact.clientX, recentImpact.clientY, `${totalEnemyImpact}`, undefined, { triggerImpactBeat: false });
        recentLungeImpactRef.current = null;
        recentPlayerCardDamageSourceRef.current = null;
      } else if (resolvedDelayedBundles.length > 0) {
        const delayedSourceTitle = String(resolvedDelayedBundles[0]?.sourceTitle ?? '').trim();
        const delayedAnchor = delayedSourceTitle ? delayedDamageAnchorByTitleRef.current[delayedSourceTitle] : undefined;
        const portraitRect = portraitMaskRef.current?.getBoundingClientRect() ?? hudRef.current?.getBoundingClientRect() ?? null;
        const fallbackX = portraitRect ? (portraitRect.left + portraitRect.width * 0.62) : (typeof window !== 'undefined' ? (window.innerWidth * 0.62) : 640);
        const fallbackY = portraitRect ? (portraitRect.top + portraitRect.height * 0.46) : (typeof window !== 'undefined' ? (window.innerHeight * 0.34) : 280);
        addImpactFeedback(delayedAnchor?.clientX ?? fallbackX, delayedAnchor?.clientY ?? fallbackY, `${totalEnemyImpact}`, 'Delayed Hit');
        recentPlayerCardDamageSourceRef.current = null;
      } else if (recentPlayerCardImpactActive && recentPlayerCardSource) {
        addImpactFeedback(recentPlayerCardSource.clientX, recentPlayerCardSource.clientY, `${totalEnemyImpact}`);
        recentPlayerCardDamageSourceRef.current = null;
      }

    }

    if (playerSpLoss > 0) {
      pulseSeqRef.current += 1;
      setPlayerSpPulseTick(pulseSeqRef.current);
    }

    prevStateRef.current = state;
  }, [state, addFloatingFeedback, addImpactFeedback]);



  // --- Portrait-fit metrics (object-fit: contain) ---
  // We compute the *rendered bitmap* rect inside the portrait element so we can
  // anchor enemy HUD + chips to the portrait edge across resolutions.
  const portraitRef = React.useRef<HTMLImageElement | null>(null);
  const portraitMaskRef = React.useRef<HTMLDivElement | null>(null);
  const hudRef = React.useRef<HTMLDivElement | null>(null);
  const chipsRef = React.useRef<HTMLDivElement | null>(null);

  // Keep layout stable even when chips are temporarily absent/0-width.
  // (e.g. enemies with no statuses, or when chips mount one frame later)
  const lastBaseHudWRef = React.useRef<number>(0);
  const lastBaseChipsWRef = React.useRef<number>(0);
  const lastReqSideRef = React.useRef<number>(0);

  // Reset cached measurements when the enemy changes so we don't carry a large
  // gutter requirement from a previous enemy into the next fight.
  React.useEffect(() => {
    lastBaseHudWRef.current = 0;
    lastBaseChipsWRef.current = 0;
    lastReqSideRef.current = 0;
  }, [state.enemyId]);

  const [hudInside, setHudInside] = React.useState(false);
  const [chipsInside, setChipsInside] = React.useState(false);
  const [portraitFit, setPortraitFit] = React.useState<{
    x: number;
    y: number;
    w: number;
    h: number;
    cw: number;
    ch: number;
  }>({ x: 0, y: 0, w: 0, h: 0, cw: 0, ch: 0 });

  const getEnemyPortraitPreImpactAnchor = React.useCallback(() => {
    const viewportRect = impactViewportRectRef.current;
    const portraitRect = portraitMaskRef.current?.getBoundingClientRect() ?? null;
    if (!viewportRect || !portraitRect) return null;
    if (!(portraitFit.w > 0 && portraitFit.h > 0)) return null;

    const clientX = portraitRect.left + portraitFit.x + portraitFit.w * PREIMPACT_VFX.centerXFactor;
    const clientY = portraitRect.top + portraitFit.y + portraitFit.h * PREIMPACT_VFX.centerYFactor;

    return {
      clientX,
      clientY,
      localX: clientX - viewportRect.left,
      localY: clientY - viewportRect.top,
    };
  }, [portraitFit.h, portraitFit.w, portraitFit.x, portraitFit.y]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleLungePreImpact = (event: Event) => {
      const detail = (event as CustomEvent<LungePreImpactEventDetail>).detail;
      const requestedDurationMs = Number(detail?.durationMs);
      const durationMs = Number.isFinite(requestedDurationMs)
        ? Math.max(120, Math.round(requestedDurationMs))
        : Math.round((PREIMPACT_VFX.frameCount * 1000) / PREIMPACT_VFX.fps);

      pulseSeqRef.current += 1;
      setEnemyPreImpactVfxState({ tick: pulseSeqRef.current, frame: 0, durationMs });
    };

    const handleLungeImpact = (event: Event) => {
      const detail = (event as CustomEvent<LungeImpactEventDetail>).detail;
      const clientX = Number(detail?.clientX);
      const clientY = Number(detail?.clientY);
      recentLungeImpactRef.current = Number.isFinite(clientX) && Number.isFinite(clientY)
        ? {
            at: typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now(),
            cardId: detail?.cardId,
            cardTitle: detail?.cardTitle,
            clientX,
            clientY,
          }
        : null;
      if (recentLungeImpactRef.current) {
        const preImpactAnchor = getEnemyPortraitPreImpactAnchor();
        triggerEnemyImpactBeat(
          preImpactAnchor?.clientX ?? recentLungeImpactRef.current.clientX,
          preImpactAnchor?.clientY ?? recentLungeImpactRef.current.clientY,
          0,
        );
      }
      pendingAttackImpactShakeRef.current = false;
    };

    const handleDelayedDamageSource = (event: Event) => {
      const detail = (event as CustomEvent<DelayedDamageSourceEventDetail>).detail;
      const sourceTitle = String(detail?.sourceTitle ?? '').trim();
      const clientX = Number(detail?.clientX);
      const clientY = Number(detail?.clientY);
      if (!sourceTitle || !Number.isFinite(clientX) || !Number.isFinite(clientY)) return;

      delayedDamageAnchorByTitleRef.current[sourceTitle] = {
        clientX,
        clientY,
        at: typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now(),
      };
    };

    const handlePlayerCardDamageSource = (event: Event) => {
      const detail = (event as CustomEvent<PlayerCardDamageSourceEventDetail>).detail;
      const clientX = Number(detail?.clientX);
      const clientY = Number(detail?.clientY);
      if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return;

      recentPlayerCardDamageSourceRef.current = {
        at: typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now(),
        cardId: detail?.cardId,
        cardTitle: detail?.cardTitle,
        clientX,
        clientY,
      };
    };

    window.addEventListener(LUNGE_PREIMPACT_EVENT, handleLungePreImpact as EventListener);
    window.addEventListener(LUNGE_IMPACT_EVENT, handleLungeImpact as EventListener);
    window.addEventListener(DELAYED_DAMAGE_SOURCE_EVENT, handleDelayedDamageSource as EventListener);
    window.addEventListener(PLAYER_CARD_DAMAGE_SOURCE_EVENT, handlePlayerCardDamageSource as EventListener);
    return () => {
      window.removeEventListener(LUNGE_PREIMPACT_EVENT, handleLungePreImpact as EventListener);
      window.removeEventListener(LUNGE_IMPACT_EVENT, handleLungeImpact as EventListener);
      window.removeEventListener(DELAYED_DAMAGE_SOURCE_EVENT, handleDelayedDamageSource as EventListener);
      window.removeEventListener(PLAYER_CARD_DAMAGE_SOURCE_EVENT, handlePlayerCardDamageSource as EventListener);
    };
  }, [getEnemyPortraitPreImpactAnchor, triggerEnemyImpactBeat]);

  React.useEffect(() => {
    enemyPreImpactVfxTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
    enemyPreImpactVfxTimeoutsRef.current = [];

    if (!enemyPreImpactVfxState) return;

    const requestedDurationMs = Number(enemyPreImpactVfxState.durationMs);
    const totalDurationMs = Number.isFinite(requestedDurationMs)
      ? Math.max(120, requestedDurationMs)
      : Math.round((PREIMPACT_VFX.frameCount * 1000) / PREIMPACT_VFX.fps);
    const frameDurationMs = totalDurationMs / PREIMPACT_VFX.frameCount;
    const { tick, durationMs } = enemyPreImpactVfxState;

    for (let frame = 1; frame < PREIMPACT_VFX.frameCount; frame += 1) {
      const timeoutId = window.setTimeout(() => {
        setEnemyPreImpactVfxState((prev) => (prev && prev.tick === tick ? { tick, frame, durationMs } : prev));
      }, Math.round(frame * frameDurationMs));
      enemyPreImpactVfxTimeoutsRef.current.push(timeoutId);
    }

    const cleanupId = window.setTimeout(() => {
      setEnemyPreImpactVfxState((prev) => (prev && prev.tick === tick ? null : prev));
    }, Math.round(totalDurationMs) + 34);
    enemyPreImpactVfxTimeoutsRef.current.push(cleanupId);

    return () => {
      enemyPreImpactVfxTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      enemyPreImpactVfxTimeoutsRef.current = [];
    };
  }, [enemyPreImpactVfxState?.tick]);

  React.useEffect(() => {
    if (enemyHitTick <= 0) return;
    const el = portraitMaskRef.current;
    if (!el || typeof el.animate !== 'function') return;

    const animation = el.animate(
      [
        { offset: 0, transform: 'translate3d(0px, 0px, 0) rotate(0deg) scale(1)' },
        { offset: 0.05, transform: 'translate3d(-4px, -4px, 0) rotate(-0.12deg) scale(1.002)' },
        { offset: 0.11, transform: 'translate3d(3px, 4px, 0) rotate(0.22deg) scale(1.017)' },
        { offset: 0.17, transform: 'translate3d(-2px, -3px, 0) rotate(-0.54deg) scale(1.015)' },
        { offset: 0.23, transform: 'translate3d(4px, 5px, 0) rotate(0.46deg) scale(1.013)' },
        { offset: 0.3, transform: 'translate3d(-3px, -3px, 0) rotate(-0.11deg) scale(1.011)' },
        { offset: 0.38, transform: 'translate3d(2px, 2px, 0) rotate(0.3deg) scale(1.009)' },
        { offset: 0.47, transform: 'translate3d(-2px, -2px, 0) rotate(-0.9deg) scale(1.008)' },
        { offset: 0.57, transform: 'translate3d(2px, 2px, 0) rotate(0.19deg) scale(1.006)' },
        { offset: 0.68, transform: 'translate3d(-2px, -2px, 0) rotate(-0.5deg) scale(1.004)' },
        { offset: 0.79, transform: 'translate3d(1px, 3px, 0) rotate(0.1deg) scale(1.003)' },
        { offset: 0.9, transform: 'translate3d(-1px, -2px, 0) rotate(-0.05deg) scale(1.0015)' },
        { offset: 1, transform: 'translate3d(0px, 0px, 0) rotate(0deg) scale(1)' },
      ],
      { duration: 300, easing: 'linear' }
    );

    return () => animation.cancel();
  }, [enemyHitTick]);


  React.useEffect(() => {
    if (playerDamageFlashTick <= 0) return;
    const el = bottomRef.current;
    if (!el || typeof el.animate !== 'function') return;

    const animation = el.animate(
      [
        { offset: 0, transform: 'translate3d(0px, 0px, 0) rotate(0deg) scale(1)' },
        { offset: 0.05, transform: 'translate3d(-32px, -32px, 0) rotate(-0.12deg) scale(1.002)' },
        { offset: 0.11, transform: 'translate3d(24px, 24px, 0) rotate(0.22deg) scale(1.017)' },
        { offset: 0.17, transform: 'translate3d(-2px, -3px, 0) rotate(-0.54deg) scale(1.015)' },
        { offset: 0.23, transform: 'translate3d(32px, 40px, 0) rotate(0.46deg) scale(1.013)' },
        { offset: 0.3, transform: 'translate3d(-24px, -24px, 0) rotate(-0.11deg) scale(1.011)' },
        { offset: 0.38, transform: 'translate3d(2px, 2px, 0) rotate(0.3deg) scale(1.009)' },
        { offset: 0.47, transform: 'translate3d(-2px, -2px, 0) rotate(-0.9deg) scale(1.008)' },
        { offset: 0.57, transform: 'translate3d(2px, 2px, 0) rotate(0.19deg) scale(1.006)' },
        { offset: 0.68, transform: 'translate3d(-2px, -2px, 0) rotate(-0.5deg) scale(1.004)' },
        { offset: 0.79, transform: 'translate3d(1px, 3px, 0) rotate(0.1deg) scale(1.003)' },
        { offset: 0.9, transform: 'translate3d(-1px, -2px, 0) rotate(-0.05deg) scale(1.0015)' },
        { offset: 1, transform: 'translate3d(0px, 0px, 0) rotate(0deg) scale(1)' },
      ],
      { duration: 300, easing: 'linear' }
    );

    return () => animation.cancel();
  }, [playerDamageFlashTick]);

  React.useEffect(() => {
    enemyImpactVfxTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
    enemyImpactVfxTimeoutsRef.current = [];

    if (!enemyImpactVfxState) return;

    const frameDurationMs = 1000 / ENEMY_HIT_VFX.fps;
    const { tick, x, y } = enemyImpactVfxState;

    const startFrame = Math.max(0, Math.min(ENEMY_HIT_VFX.frameCount - 1, ENEMY_HIT_VFX.startFrameAtDamageNumber));

    for (let frame = startFrame + 1; frame < ENEMY_HIT_VFX.frameCount; frame += 1) {
      const timeoutId = window.setTimeout(() => {
        setEnemyImpactVfxState((prev) => (prev && prev.tick === tick ? { tick, frame, x, y } : prev));
      }, Math.round((frame - startFrame) * frameDurationMs));
      enemyImpactVfxTimeoutsRef.current.push(timeoutId);
    }

    const cleanupId = window.setTimeout(() => {
      setEnemyImpactVfxState((prev) => (prev && prev.tick === tick ? null : prev));
    }, Math.round((ENEMY_HIT_VFX.frameCount - startFrame) * frameDurationMs) + 34);
    enemyImpactVfxTimeoutsRef.current.push(cleanupId);

    return () => {
      enemyImpactVfxTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      enemyImpactVfxTimeoutsRef.current = [];
      enemyPreImpactVfxTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      enemyPreImpactVfxTimeoutsRef.current = [];
    };
  }, [enemyImpactVfxState?.tick]);

  const sceneStyle = React.useMemo(() => {
    const vars: any = {
      '--enemy-hud-scale': String(enemyHudScale),
      '--enemy-chips-scale': String(enemyChipScale),
      backgroundImage: `url(${combatBackgroundImg})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center center',
      backgroundRepeat: 'no-repeat',
    };
    if (mobileSideGutter !== null) vars['--portrait-side-gutter'] = `${mobileSideGutter}px`;
    return vars as React.CSSProperties;
  }, [mobileSideGutter, enemyHudScale, enemyChipScale]);

  React.useLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    let raf = 0;
    let ro: ResizeObserver | null = null;
    const node = sceneRef.current;
    if (!node) return;

    const updateImpactViewportRect = () => {
      const currentNode = sceneRef.current;
      if (!currentNode) return;
      const rect = currentNode.getBoundingClientRect();
      const nextRect: ImpactViewportRect = {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      };
      impactViewportRectRef.current = nextRect;
      setImpactViewportRect((prev) => {
        if (
          prev &&
          prev.left === nextRect.left &&
          prev.top === nextRect.top &&
          prev.width === nextRect.width &&
          prev.height === nextRect.height
        ) {
          return prev;
        }
        return nextRect;
      });
    };

    const requestUpdate = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        updateImpactViewportRect();
      });
    };

    updateImpactViewportRect();

    window.addEventListener('resize', requestUpdate);
    window.addEventListener('scroll', requestUpdate, true);
    window.visualViewport?.addEventListener?.('resize', requestUpdate);
    window.visualViewport?.addEventListener?.('scroll', requestUpdate);

    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(requestUpdate);
      ro.observe(node);
    }

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener('resize', requestUpdate);
      window.removeEventListener('scroll', requestUpdate, true);
      window.visualViewport?.removeEventListener?.('resize', requestUpdate);
      window.visualViewport?.removeEventListener?.('scroll', requestUpdate);
    };
  }, []);


  const updatePortraitFit = React.useCallback(() => {
    const img = portraitRef.current;
    if (!img) return;

    const cw = img.clientWidth;
    const ch = img.clientHeight;
    const nw = img.naturalWidth || 0;
    const nh = img.naturalHeight || 0;

    if (!cw || !ch || !nw || !nh) return;

    // Same math the browser uses for object-fit: contain.
    const scale = Math.min(cw / nw, ch / nh);
    const w = nw * scale;
    const h = nh * scale;
    const x = (cw - w) / 2;
    const y = (ch - h) / 2;

    setPortraitFit({ x, y, w, h, cw, ch });
  }, []);

  React.useEffect(() => {
    // Recompute when the portrait asset changes (enemy swap).
    updatePortraitFit();
  }, [scenePortrait, updatePortraitFit]);

  React.useEffect(() => {
    const img = portraitRef.current;
    if (!img) return;

    updatePortraitFit();

    // Keep it stable across window resizes / layout changes.
    const onResize = () => updatePortraitFit();
    window.addEventListener('resize', onResize);

    if (typeof ResizeObserver === 'undefined') {
      return () => window.removeEventListener('resize', onResize);
    }

    const ro = new ResizeObserver(() => updatePortraitFit());
    ro.observe(img);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [updatePortraitFit]);

  // --- Responsive: keep HUD/Chips anchored to the portrait edges, and scale them down on tight screens.
  // On mobile we also reserve equal left/right gutters so the portrait stays centered between them.
  React.useLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    const mqlMobile = window.matchMedia ? window.matchMedia('(max-width: 768px)') : null;
    let raf = 0;
    let ro: ResizeObserver | null = null;

    const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

    const recompute = () => {
      const hudEl = hudRef.current;
      const chipsEl = chipsRef.current;
      const img = portraitRef.current;
      if (!hudEl || !chipsEl || !img) return;

      const vw = window.innerWidth || document.documentElement.clientWidth || 0;
      if (!vw) return;

      const isMobile = !!mqlMobile && mqlMobile.matches;

      const hudStyle = getComputedStyle(hudEl);
      const chipsStyle = getComputedStyle(chipsEl);
      const hudGap = parseFloat(hudStyle.getPropertyValue('--enemy-ui-gap-x')) || (isMobile ? 8 : 10);
      const chipsGap = parseFloat(chipsStyle.getPropertyValue('--enemy-chip-gap-x')) || (isMobile ? 8 : 10);

      // Measure *current* widths, then approximate unscaled sizes.
      // Do NOT bail when chipsW is 0: we keep a stable gutter even when there
      // are no active status chips (or when the list mounts slightly later).
      const hudWNow = hudEl.getBoundingClientRect().width || 0;
      const chipsWNow = chipsEl.getBoundingClientRect().width || 0;

      if (hudWNow > 0) lastBaseHudWRef.current = hudWNow / (enemyHudScale || 1);
      if (chipsWNow > 0) lastBaseChipsWRef.current = chipsWNow / (enemyChipScale || 1);

      const baseHudW = lastBaseHudWRef.current || 0;
      const baseChipsW = (chipsWNow > 0 ? lastBaseChipsWRef.current : lastBaseChipsWRef.current) || 0;
      if (!baseHudW) return;

      // Breathing room beyond the raw widths.
      // Slightly larger on mobile to prevent "kissing" the portrait frame.
      const extra = isMobile ? 22 : 14;
      const reqHud = baseHudW + hudGap + extra;
      const reqChips = baseChipsW + chipsGap + extra;

      // Keep the gutter stable even when the enemy has *no* current chips.
      // If chips are 0-width, don't let the reserved right gutter collapse.
      const computedSide = Math.max(reqHud, reqChips);
      if (chipsWNow > 0) {
        lastReqSideRef.current = Math.max(lastReqSideRef.current, computedSide);
      } else {
        lastReqSideRef.current = Math.max(lastReqSideRef.current, reqHud);
      }

      // By default we try to keep both sides at the same scale for a balanced look.
      let nextHudScale = 1;
      let nextChipScale = 1;
      let nextGutter: number | null = null;

      if (isMobile) {
        // Mobile: reserve *equal* gutters and scale HUD+chips proportionally
        // so they fit, prioritizing keeping the portrait centered and readable.
        const reqSide = lastReqSideRef.current || Math.max(reqHud, reqChips);

        // Hard minimum portrait width (prevents shrinking too far).
        const hardMinPortraitW = 170;
        const maxGutterHard = Math.max(0, Math.floor((vw - hardMinPortraitW) / 2));

        // Target gutter budget favors a larger portrait (less "squeezing").
        // We then scale side UI to fit inside that budget.
        const targetGutter = clamp(Math.round(vw * 0.20), 78, 150);
        let gutter = Math.min(maxGutterHard, targetGutter);

        // If needed, allow gutters to expand (shrinking portrait) before we ever overlap.
        const minReadable = 0.65; // <-- keeps chips from looking squished
        const needAtMin = Math.ceil(reqSide * minReadable);
        if (gutter < needAtMin) gutter = Math.min(maxGutterHard, needAtMin);

        const scale = reqSide > 0 ? Math.min(1, gutter / reqSide) : 1;
        nextHudScale = scale;
        nextChipScale = scale;
        nextGutter = gutter;

        // On mobile we always keep the UI in the reserved gutters.
        setHudInside(false);
        setChipsInside(false);
      } else {
        // Desktop/tablet: anchor to portrait edges.
        // On narrower desktop screens we *also* reserve equal side gutters (like mobile)
        // so the HUD/chips never have to overlap the portrait.
        if (!(portraitFit.w > 0 && portraitFit.h > 0)) return;

        const rect = img.getBoundingClientRect();
        const bitmapLeft = rect.left + portraitFit.x;
        const bitmapRight = rect.left + portraitFit.x + portraitFit.w;
        const safe = 6;

        const availLeft = Math.max(0, bitmapLeft - safe);
        const availRight = Math.max(0, (vw - safe) - bitmapRight);

        // If either side is tight, reserve gutters and scale down as needed
        // (prevents chips/HUD from ever overlapping the portrait on small screens).
        if (availLeft < reqHud || availRight < reqChips) {
          const reqSide = lastReqSideRef.current || Math.max(reqHud, reqChips);

          const hardMinPortraitW = Math.max(360, Math.min(680, Math.round(vw * 0.62)));
          const maxGutterHard = Math.max(0, Math.floor((vw - hardMinPortraitW) / 2));

          // Prefer keeping portrait large; scale side UI into the gutter.
          const targetGutter = clamp(Math.round(vw * 0.12), 84, 160);
          let gutter = Math.min(maxGutterHard, Math.max(80, targetGutter));

          const minReadable = 0.55;
          const needAtMin = Math.ceil(reqSide * minReadable);
          if (gutter < needAtMin) gutter = Math.min(maxGutterHard, needAtMin);

          const scale = reqSide > 0 ? Math.min(1, gutter / reqSide) : 1;

          nextHudScale = scale;
          nextChipScale = scale;
          nextGutter = gutter;

          // With gutters reserved, keep both blocks outside the portrait.
          setHudInside(false);
          setChipsInside(false);
        } else {
          // Wider screens: no gutters; just scale to fit the available letterbox space.
          const scaleLeft = reqHud > 0 ? (availLeft / reqHud) : 1;
          const scaleRight = reqChips > 0 ? (availRight / reqChips) : 1;
          const target = Math.min(1, scaleLeft, scaleRight);
          const clamped = clamp(target, 0.70, 1);

          nextHudScale = clamped;
          nextChipScale = clamped;
          nextGutter = null;

          // If even the minimum scale can't prevent overlap, fall back to placing inside.
          const needHud = baseHudW * clamped + hudGap;
          const needChips = baseChipsW * clamped + chipsGap;

          setHudInside(bitmapLeft - needHud < safe);
          setChipsInside(bitmapRight + needChips > (vw - safe));
        }
      }

      // Apply changes only when they materially change layout, to avoid render loops.
      setMobileSideGutter((prev) => {
        if (nextGutter === null) return prev === null ? prev : null;
        return prev === null || Math.abs(prev - nextGutter) >= 1 ? nextGutter : prev;
      });
      setEnemyHudScale((prev) => (Math.abs(prev - nextHudScale) > 0.02 ? nextHudScale : prev));
      setEnemyChipScale((prev) => (Math.abs(prev - nextChipScale) > 0.02 ? nextChipScale : prev));
    };

    const schedule = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(recompute);
    };

    schedule();
    window.addEventListener('resize', schedule);

    const onMqlChange = () => schedule();
    if (mqlMobile) {
      if (typeof mqlMobile.addEventListener === 'function') mqlMobile.addEventListener('change', onMqlChange);
      else if (typeof (mqlMobile as any).addListener === 'function') (mqlMobile as any).addListener(onMqlChange);
    }

    if (typeof ResizeObserver !== 'undefined') {
      const hudEl = hudRef.current;
      const chipsEl = chipsRef.current;
      const imgEl = portraitRef.current;
      ro = new ResizeObserver(() => schedule());
      if (hudEl) ro.observe(hudEl);
      if (chipsEl) ro.observe(chipsEl);
      if (imgEl) ro.observe(imgEl);
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', schedule);
      if (mqlMobile) {
        if (typeof mqlMobile.removeEventListener === 'function') mqlMobile.removeEventListener('change', onMqlChange);
        else if (typeof (mqlMobile as any).removeListener === 'function') (mqlMobile as any).removeListener(onMqlChange);
      }
      if (ro) ro.disconnect();
    };
  }, [scenePortrait, portraitFit.w, portraitFit.h, state.turn, enemyHudScale, enemyChipScale]);




  const playerTurnHandoffKey = state.phase === 'player' ? `player:${state.turn}` : '';
  const pendingPlayerTurnHandoff = !!playerTurnHandoffKey && lastShownPlayerTurnHandoffKeyRef.current !== playerTurnHandoffKey;
  const turnHandoffLocked = !!activeTurnHandoffBanner || pendingPlayerTurnHandoff || suppressInitialTurnHandoff;

  const playerIsStunned = (state.player?.stunned ?? 0) > 0;

  const canTakeTurnAction =
    isPlayerTurn &&
    state.canPlay &&
    !discardMode &&
    !peekMode &&
    !topPickMode &&
    !choiceMode &&
    !xMode &&
    !tutorMode &&
    !turnHandoffLocked;

  // Centralize derived combat values to avoid redundant computations in children.
  const combatDeckCount = Array.isArray((state as any).deck) ? (state as any).deck.length : state.physicalDeck.length + state.magicDeck.length;
  const combatDiscardCount =
    Array.isArray((state as any).discard) ? (state as any).discard.length : state.physicalDiscard.length + state.magicDiscard.length;

  // Snapshot the player status for StatusChips. See StatusChips.tsx for the shape.
  const playerStatusSnapshot = React.useMemo<PlayerStatusSnapshot>(() => {
    const playerDelayed = Array.isArray(state.delayed)
      ? state.delayed.filter((d: any) => (d?.owner ?? 'player') !== 'enemy')
      : [];
    const delayedCount = playerDelayed.length;
    const delayedMinTurns =
      delayedCount > 0
        ? Math.min(...playerDelayed.map((d: any) => Math.max(0, Number(d?.turnsLeft) || 0)))
        : 0;
    return {
      stunned: Math.max(0, Number(state.player.stunned ?? 0) || 0),
      reflectPct: state.player.reflectPct,
      negateNextDamage: state.player.negateNextDamage,
      nextAttackBonus: state.nextAttackBonus,
      nextCardCost0: state.nextCardCost0,
      playerDamageMultThisTurn: state.playerDamageMultThisTurn,
      playerIncomingDamageMultNextTurn: state.playerIncomingDamageMultNextTurn,
      nextMagicCardCostDoubleSP: state.nextMagicCardCostDoubleSP,
      playerWeakenedTurns: state.playerWeakenedTurns,
      activeCompound: String((state as any).activeCompound ?? ''),
      darkApostleCursedFateActive: !!((state as any).darkApostleCursedFateActive),
      phase: state.phase,
      canPlay: state.canPlay,
      playerPlaysThisTurn: state.playerPlaysThisTurn,
      pendingDiscardRemaining: state.pendingDiscard?.remaining ?? 0,
      delayedCount,
      delayedMinTurns,
      physicalEscalation: state.physicalEscalation,
      magicEscalation: state.magicEscalation,
      custodianForfeitureTurns: Math.max(0, Number((state as any).custodianForfeitureTurns ?? 0) || 0),
    };
  }, [
    state.player.stunned,
    state.player.reflectPct,
    state.player.negateNextDamage,
    state.nextAttackBonus,
    state.nextCardCost0,
    state.playerDamageMultThisTurn,
    state.playerIncomingDamageMultNextTurn,
    state.nextMagicCardCostDoubleSP,
    state.playerWeakenedTurns,
    (state as any).activeCompound,
    (state as any).darkApostleCursedFateActive,
    state.phase,
    state.canPlay,
    state.playerPlaysThisTurn,
    state.pendingDiscard?.remaining,
    state.delayed,
    state.physicalEscalation,
    state.magicEscalation,
    (state as any).custodianForfeitureTurns,
  ]);

  // Snapshot the enemy status for StatusChips.
    const enemyStatusSnapshot = React.useMemo<EnemyStatusSnapshot>(() => {
    const relicMods = ((state as any)?.relicMods ?? {}) as any;
    const isPrimeVessel = state.enemyId === 'prime_vessel';
    const enemyDelayed = Array.isArray(state.delayed)
      ? state.delayed.filter((bundle: any) => bundle?.owner === 'enemy')
      : [];
    const deferredSanctionBundles = enemyDelayed.filter((bundle: any) => String(bundle?.sourceTitle ?? '').trim().toLowerCase() === 'deferred sanction');
    const deferredSanctionCount = deferredSanctionBundles.length;
    const deferredSanctionMinTurns = deferredSanctionCount > 0
      ? deferredSanctionBundles.reduce((min: number, bundle: any) => {
          const turns = Math.max(0, Number(bundle?.turnsLeft ?? 0) || 0);
          return Math.min(min, turns);
        }, Number.POSITIVE_INFINITY)
      : 0;
    return {
      stunned: state.enemy.stunned,
      enemyDamageMultTurns: Math.max(0, Number(state.enemyDamageMultTurns ?? 0) || 0),
      enemyDamageMult: Number(state.enemyDamageMult ?? 1) || 1,
      reflectPct: Math.max(0, Number(state.enemy.reflectPct ?? 0) || 0),
      activeCompound: String((state as any).activeCompound ?? ''),
      mercenaryPrecision: !!((state as any).mercenaryPrecision),
      mercenaryAmbush: !!((state as any).mercenaryAmbush),
      lampKeeperDisposition: String((state as any).lampKeeperDisposition ?? ''),
      lampKeeperRoomLight: String((state as any).lampKeeperRoomLight ?? ''),
      custodianForfeitureTurns: Math.max(0, Number((state as any).custodianForfeitureTurns ?? 0) || 0),
      custodianExiledAttackCount: Math.max(0, Number((state as any).custodianExiledAttackCount ?? 0) || 0),
      custodianExiledDefenseCount: Math.max(0, Number((state as any).custodianExiledDefenseCount ?? 0) || 0),
      custodianExiledUtilityCount: Math.max(0, Number((state as any).custodianExiledUtilityCount ?? 0) || 0),
      thresholdVerifierDeferredSanctionCount: deferredSanctionCount,
      thresholdVerifierDeferredSanctionMinTurns: Number.isFinite(deferredSanctionMinTurns) ? deferredSanctionMinTurns : 0,
      isPrimeVessel,
      pulseEngineActive: isPrimeVessel && !!relicMods.primeVesselPulseEngineActive,
      synapticRetortActive: isPrimeVessel && !!relicMods.primeVesselSynapticRetortActive,
      astralReliquaryActive: isPrimeVessel && !!relicMods.primeVesselAstralReliquaryActive,
      primeCrucibleActive: isPrimeVessel && !!relicMods.primeVesselPrimeCrucibleActive,
      isCustodian: state.enemyId === 'custodian',
    };
  }, [
    state.enemy.stunned,
    state.enemyDamageMultTurns,
    state.enemyDamageMult,
    state.enemy.reflectPct,
    (state as any).activeCompound,
    (state as any).mercenaryPrecision,
    (state as any).mercenaryAmbush,
    (state as any).lampKeeperDisposition,
    (state as any).lampKeeperRoomLight,
    (state as any).custodianForfeitureTurns,
    (state as any).custodianExiledAttackCount,
    (state as any).custodianExiledDefenseCount,
    (state as any).custodianExiledUtilityCount,
    state.delayed,
    state.enemyId,
    (state as any)?.relicMods,
  ]);

  const [reshuffleMode, setReshuffleMode] = React.useState(false);
  const [reshufflePick, setReshufflePick] = React.useState<string[]>([]);
  const [pendingRecastRequest, setPendingRecastRequest] = React.useState<null | { requestId: string; cardIds: string[] }>(null);

  React.useEffect(() => {
    // Any modal/prompt cancels reshuffle selection for safety.
    if (!canTakeTurnAction) {
      setReshuffleMode(false);
      setReshufflePick([]);
      if (!isPlayerTurn) {
        setPendingRecastRequest(null);
      }
      return;
    }
  }, [canTakeTurnAction, isPlayerTurn]);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !pendingRecastRequest?.requestId) return;

    const handleRecastDiscardDone = (event: Event) => {
      const detail = (event as CustomEvent<{ requestId?: string }>).detail;
      if (!detail?.requestId || detail.requestId !== pendingRecastRequest.requestId) return;
      onReshuffleSelected(pendingRecastRequest.cardIds);
      setPendingRecastRequest(null);
    };

    window.addEventListener(RECAST_SELECTION_DISCARD_DONE_EVENT, handleRecastDiscardDone as EventListener);
    return () => {
      window.removeEventListener(RECAST_SELECTION_DISCARD_DONE_EVENT, handleRecastDiscardDone as EventListener);
    };
  }, [onReshuffleSelected, pendingRecastRequest]);

  const toggleReshufflePick = React.useCallback((cardId: string) => {
    if (pendingRecastRequest) return;
    setReshufflePick((prev) => {
      if (prev.includes(cardId)) return prev.filter((x) => x !== cardId);
      return [...prev, cardId];
    });
  }, [pendingRecastRequest]);

  const commitRecastSelection = React.useCallback((ids: string[]) => {
    const cardIds = Array.from(new Set(ids.filter((value) => typeof value === 'string' && value.trim().length > 0)));
    if (cardIds.length === 0 || pendingRecastRequest) return;

    const mods = (state as any)?.relicMods as { recastDrawPenalty?: number } | undefined;
    const recastPenalty =
      typeof mods?.recastDrawPenalty === 'number'
        ? Math.max(0, Math.floor(Number(mods.recastDrawPenalty) || 0))
        : 1;
    const drawCount = Math.max(1, cardIds.length - recastPenalty);
    const requestId = `recast:${Date.now()}:${Math.random().toString(36).slice(2)}`;

    setReshuffleMode(false);
    setReshufflePick([]);
    setPendingRecastRequest({ requestId, cardIds });

    if (typeof window === 'undefined') {
      onReshuffleSelected(cardIds);
      setPendingRecastRequest(null);
      return;
    }

    dispatchRecastSelectionAnimation(requestId, cardIds, drawCount);
  }, [onReshuffleSelected, pendingRecastRequest, state]);



  const latestLog = state.log[0];
  const lastToastRef = React.useRef<string>("");
  const [toast, setToast] = React.useState<ToastMessage | null>(null);

  React.useEffect(() => {
    if (!latestLog) return;
    if (latestLog === lastToastRef.current) return;
    lastToastRef.current = latestLog;
    setToast({ text: latestLog });
  }, [latestLog]);


// Peek/Reorder modal state (e.g. Third Eye)
const peekKey = state.pendingPeek ? state.pendingPeek.cards.map((c) => c.id).join(',') : '';
const [peekOrder, setPeekOrder] = React.useState<string[]>([]);
React.useEffect(() => {
  if (!state.pendingPeek) {
    setPeekOrder([]);
    return;
  }
  setPeekOrder(state.pendingPeek.cards.map((c) => c.id));
}, [peekKey]);

const movePeek = (id: string, dir: -1 | 1) => {
  setPeekOrder((prev) => {
    const i = prev.indexOf(id);
    if (i < 0) return prev;
    const j = i + dir;
    if (j < 0 || j >= prev.length) return prev;
    const next = prev.slice();
    const tmp = next[i];
    next[i] = next[j];
    next[j] = tmp;
    return next;
  });
};

// X modal state (e.g. Blood Ritual / Forbidden Knowledge)
const xKey = state.pendingX ? `${state.pendingX.sourceTitle}-${state.pendingX.from}-${state.pendingX.to}-${state.pendingX.max}` : '';
const [xValue, setXValue] = React.useState<number>(0);
React.useEffect(() => {
  if (!state.pendingX) {
    setXValue(0);
    return;
  }
  setXValue(0);
}, [xKey]);

  // X-cost modal: cards whose SP cost is literally "X" (chosen at play time).
  const [xCostPick, setXCostPick] = React.useState<null | {
    cardId: string;
    resource: 'sp' | 'hp';
    max: number;
    value: number;
  }>(null);

  const computeEffectiveSpCostForX = React.useCallback((card: any, x: number) => {
    const cost0Count = Math.max(0, Number((state as any).nextCardCost0 ?? 0) || 0);
    const cost0Active = cost0Count > 0;
    if (cost0Active) return 0;

    const magicDoubleCount = Math.max(0, Number((state as any).nextMagicCardCostDoubleSP ?? 0) || 0);
    const spMult = (card.kind === 'magic' && magicDoubleCount > 0) ? 2 : 1;
    const magicChainStreak = Math.max(0, Number((state as any).magicChainStreak ?? 0) || 0);
    const magicChainSurcharge = card.kind === 'magic' ? magicChainStreak : 0;

    const mods = (state as any).relicMods as any;
    const hasDelay = Math.max(0, Number((card as any).delayTurns || 0) || 0) > 0
      || (Array.isArray((card as any).delayed) && (card as any).delayed.length > 0)
      || (Math.max(0, Number((card as any).costDelayTurns || 0) || 0) > 0);

    const keywords = Array.isArray((card as any).keywords) ? (card as any).keywords : [];
    const hasKw = (k: string) => keywords.some((s: any) => String(s).trim().toLowerCase() === k);
    const chainEligible = hasKw('chain') || (!!mods?.exileCardsGetChain && hasKw('exile'));

    // cost.sp stores escalation (+N on reshuffles) for X-cost cards.
    let printed = Math.max(0, Math.floor(Number(x) || 0)) + Math.max(0, Number(card.cost?.sp ?? 0) || 0);
    if (chainEligible && mods?.chainCostReduction && Number(mods.chainCostReduction) > 0) {
      printed = Math.max(0, printed - Math.max(0, Number(mods.chainCostReduction) || 0));
    }

    let cost = printed * spMult;
    if (hasDelay && mods?.delayCostReduction && Number(mods.delayCostReduction) > 0) {
      cost = Math.max(0, cost - Math.max(0, Number(mods.delayCostReduction) || 0));
    }

    cost += magicChainSurcharge;
    return Math.max(0, Math.floor(cost));
  }, [state]);

  const computeMaxAffordableX = React.useCallback((card: any) => {
    const sp = Math.max(0, Number(state.player.sp || 0) || 0);
    // Binary search for the largest X such that effectiveCost(X) <= current SP.
    let hi = Math.max(1, sp + 10);
    while (hi < 5000 && computeEffectiveSpCostForX(card, hi) <= sp) hi *= 2;
    let lo = 1;
    let best = 0;
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      if (computeEffectiveSpCostForX(card, mid) <= sp) {
        best = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return best;
  }, [computeEffectiveSpCostForX, state.player.sp]);

  const computeEffectiveHpCostForX = React.useCallback((card: any, x: number) => {
    const cost0Count = Math.max(0, Number((state as any).nextCardCost0 ?? 0) || 0);
    const cost0Active = cost0Count > 0;
    if (cost0Active) return 0;
    // cost.hp stores escalation (+N on reshuffles) for X-HP cards.
    const printed = Math.max(0, Math.floor(Number(x) || 0)) + Math.max(0, Number(card.cost?.hp ?? 0) || 0);
    return Math.max(0, Math.floor(printed));
  }, [state]);

  const computeMaxAffordableXHp = React.useCallback((card: any) => {
    const hp = Math.max(0, Number(state.player.hp || 0) || 0);
    let hi = Math.max(1, hp + 10);
    while (hi < 5000 && computeEffectiveHpCostForX(card, hi) <= hp) hi *= 2;
    let lo = 1;
    let best = 0;
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      if (computeEffectiveHpCostForX(card, mid) <= hp) {
        best = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return best;
  }, [computeEffectiveHpCostForX, state.player.hp]);

  // Addendum modal: some cards allow paying extra ${resLabel} to add an extra effect.
  const [addendumPick, setAddendumPick] = React.useState<null | {
    cardId: string;
    variable: boolean;
    fixed?: number;
    max: number;
    value: number;
    resource: 'sp' | 'hp';
  }>(null);

  const queuedPrereqPlayRef = React.useRef<null | {
    cardId: string;
    args?: PlayCommitArgs;
  }>(null);

  const getDiscardPrereqAmount = React.useCallback((card: any) => {
    const effects = Array.isArray(card?.effects) ? card.effects : [];
    return effects
      .filter((effect: any) => effect?.type === 'discard')
      .reduce((sum: number, effect: any) => sum + Math.max(0, Number(effect?.amount) || 0), 0);
  }, []);

  const cardNeedsDiscardPrereq = React.useCallback((card: any) => getDiscardPrereqAmount(card) > 0, [getDiscardPrereqAmount]);

  const canPayDiscardPrereq = React.useCallback((card: any) => {
    const discardNeeded = getDiscardPrereqAmount(card);
    if (discardNeeded <= 0) return true;
    const cardsInHand = [state.hand.main.physical, state.hand.main.magic, ...state.hand.extra].filter(Boolean).length;
    const availableOtherCards = Math.max(0, cardsInHand - 1);
    return availableOtherCards >= discardNeeded;
  }, [getDiscardPrereqAmount, state.hand]);

  const canAffordImmediateCard = React.useCallback((card: any) => {
    const cost0Count = Math.max(0, Number((state as any).nextCardCost0 ?? 0) || 0);
    const cost0Active = cost0Count > 0;

    let hpCost = cost0Active ? 0 : Math.max(0, Number(card?.cost?.hp ?? 0) || 0);
    let spCost = cost0Active ? 0 : Math.max(0, Number(card?.cost?.sp ?? 0) || 0);

    const magicDoubleCount = Math.max(0, Number((state as any).nextMagicCardCostDoubleSP ?? 0) || 0);
    const magicChainStreak = Math.max(0, Number((state as any).magicChainStreak ?? 0) || 0);
    const mods = (state as any).relicMods as any;
    const hasDelay = Math.max(0, Number((card as any)?.delayTurns || 0) || 0) > 0
      || (Array.isArray((card as any)?.delayed) && (card as any).delayed.length > 0)
      || (Math.max(0, Number((card as any)?.costDelayTurns || 0) || 0) > 0);
    const keywords = Array.isArray((card as any)?.keywords) ? (card as any).keywords : [];
    const hasKw = (k: string) => keywords.some((s: any) => String(s).trim().toLowerCase() === k);
    const chainEligible = hasKw('chain') || (!!mods?.exileCardsGetChain && hasKw('exile'));

    if (!cost0Active && chainEligible && mods?.chainCostReduction && Number(mods.chainCostReduction) > 0) {
      spCost = Math.max(0, spCost - Math.max(0, Number(mods.chainCostReduction) || 0));
    }

    if (!cost0Active && card?.kind === 'magic' && magicDoubleCount > 0) {
      spCost *= 2;
    }

    if (!cost0Active && hasDelay && mods?.delayCostReduction && Number(mods.delayCostReduction) > 0) {
      spCost = Math.max(0, spCost - Math.max(0, Number(mods.delayCostReduction) || 0));
    }

    if (!cost0Active && card?.kind === 'magic') {
      spCost += magicChainStreak;
    }

    return state.player.hp >= hpCost && state.player.sp >= spCost;
  }, [state]);

  const queuePlayAnimationAfterPrereq = React.useCallback((cardId: string, args?: PlayCommitArgs) => {
    const all = [state.hand.main.physical, state.hand.main.magic, ...state.hand.extra].filter(Boolean) as any[];
    const card = all.find((candidate) => candidate.id === cardId);
    if (card && isAttackCard(card)) pendingAttackImpactShakeRef.current = true;
    queuedPrereqPlayRef.current = {
      cardId,
      args,
    };
    if (onPrepareDiscardPrereqPlay) {
      onPrepareDiscardPrereqPlay(cardId, args?.addendumPay, args?.xPaySp, args?.addendumResource, args?.xPayHp);
      return;
    }
    // Fallback only to preserve runtime safety when an older caller still mounts
    // Board without the new prereq callback. This restores previous behavior
    // rather than crashing, but it does NOT fix Power Surge timing.
    onPlay(cardId, args?.addendumPay, args?.xPaySp, args?.addendumResource, args?.xPayHp);
  }, [onPlay, onPrepareDiscardPrereqPlay, state.hand]);

  const resolvePlayInvokeVariant = React.useCallback((cardId: string, args?: PlayCommitArgs): InvokeAnimationVariant | null => {
    const all = [state.hand.main.physical, state.hand.main.magic, ...state.hand.extra].filter(Boolean) as any[];
    const card = all.find((candidate) => candidate.id === cardId);
    if (!card) return null;

    return resolveInvokeAnimationVariantForPlay({
      state,
      card,
      addendumPaySp: Math.max(0, Number(args?.addendumResource === 'hp' ? 0 : (args?.addendumPay ?? 0)) || 0),
      addendumPayHp: Math.max(0, Number(args?.addendumResource === 'hp' ? (args?.addendumPay ?? 0) : 0) || 0),
      xPaySp: Math.max(0, Number(args?.xPaySp ?? 0) || 0),
      xPayHp: Math.max(0, Number(args?.xPayHp ?? 0) || 0),
    });
  }, [state]);

  const dispatchPlayAfterUiPrereqs = React.useCallback((cardId: string, args?: PlayCommitArgs) => {
    const all = [state.hand.main.physical, state.hand.main.magic, ...state.hand.extra].filter(Boolean) as any[];
    const card = all.find((candidate) => candidate.id === cardId);
    if (!card) return;

    if (cardNeedsDiscardPrereq(card) && !canPayDiscardPrereq(card)) {
      const discardNeeded = getDiscardPrereqAmount(card);
      setToast({ text: `Not enough cards in hand to discard ${discardNeeded} for ${card.title}.` });
      return;
    }

    if (isAttackCard(card)) pendingAttackImpactShakeRef.current = true;

    if (cardNeedsDiscardPrereq(card)) {
      queuePlayAnimationAfterPrereq(cardId, args);
      return;
    }

    dispatchDeferredPlay(cardId, args, false, resolvePlayInvokeVariant(cardId, args));
  }, [canPayDiscardPrereq, cardNeedsDiscardPrereq, getDiscardPrereqAmount, queuePlayAnimationAfterPrereq, resolvePlayInvokeVariant, state.hand]);

  React.useEffect(() => {
    const queued = queuedPrereqPlayRef.current;
    if (!queued) return;

    const hasPendingPrereq = (!!state.pendingDiscard && state.pendingDiscard.remaining > 0)
      || !!state.pendingPeek
      || !!(state as any).pendingTopPick
      || !!state.pendingChoice
      || !!state.pendingTutor
      || !!state.pendingX;

    const pendingPrereqCardId = (state as any).pendingPrereqPlay?.cardId as string | undefined;
    if (hasPendingPrereq || pendingPrereqCardId !== queued.cardId) return;

    dispatchDeferredPlay(queued.cardId, queued.args, false, resolvePlayInvokeVariant(queued.cardId, queued.args));
    queuedPrereqPlayRef.current = null;
  }, [resolvePlayInvokeVariant, state]);

  const handlePlayRequest = React.useCallback((cardId: string): HandPlayRequestResult => {
    if (discardMode) {
      return { kind: 'immediate' };
    }

    if (reshuffleMode) {
      return { kind: 'blocked' };
    }

    if (!isPlayerTurn || !state.canPlay) {
      return { kind: 'blocked' };
    }

    const all = [state.hand.main.physical, state.hand.main.magic, ...state.hand.extra].filter(Boolean) as any[];
    const card = all.find((c) => c.id === cardId);
    if (!card) return { kind: 'blocked' };

    const unauthorizedLocks = Array.isArray((state as any).unauthorizedPassageLocks) ? ((state as any).unauthorizedPassageLocks as Array<any>) : [];
    const unauthorizedTurns = Math.max(0, Number(unauthorizedLocks.find((entry) => entry?.cardId === cardId)?.turns ?? 0) || 0);
    if (unauthorizedTurns > 0) {
      return { kind: 'blocked' };
    }

    if (cardNeedsDiscardPrereq(card) && !canPayDiscardPrereq(card)) {
      const discardNeeded = getDiscardPrereqAmount(card);
      setToast({ text: `Not enough cards in hand to discard ${discardNeeded} for ${card.title}.` });
      return { kind: 'blocked' };
    }

    if (!(card as any).spCostIsX && !(card as any).hpCostIsX && !canAffordImmediateCard(card)) {
      setToast({ text: `Not enough resources to play ${card.title}.` });
      return { kind: 'blocked' };
    }

    // X-cost cards: SP cost is "X" and must be chosen at play time.
    if ((card as any).spCostIsX && isPlayerTurn && state.canPlay) {
      const cost0Count = Math.max(0, Number((state as any).nextCardCost0 ?? 0) || 0);
      const cost0Active = cost0Count > 0;
      if (cost0Active) {
        // If a "next card costs 0" effect is active, we force X=0 (engine allows this only when cost0 is active).
        return { kind: 'immediate', args: { addendumPay: 0, xPaySp: 0, xPayHp: 0 }, invokeVariant: resolvePlayInvokeVariant(cardId, { addendumPay: 0, xPaySp: 0, xPayHp: 0 }) };
      }

      const max = computeMaxAffordableX(card);
      if (max <= 0) {
        setToast({ text: `Not enough SP to play ${card.title}.` });
        return { kind: 'blocked' };
      }

      setXCostPick({ cardId, resource: 'sp', max, value: Math.min(1, max) });
      return { kind: 'deferred' };
    }

    // X-cost cards: HP cost is "X" and must be chosen at play time.
    if ((card as any).hpCostIsX && isPlayerTurn && state.canPlay) {
      const cost0Count = Math.max(0, Number((state as any).nextCardCost0 ?? 0) || 0);
      const cost0Active = cost0Count > 0;
      if (cost0Active) {
        return { kind: 'immediate', args: { addendumPay: 0, xPaySp: 0, xPayHp: 0 }, invokeVariant: resolvePlayInvokeVariant(cardId, { addendumPay: 0, xPaySp: 0, xPayHp: 0 }) };
      }

      const max = computeMaxAffordableXHp(card);
      if (max <= 0) {
        setToast({ text: `Not enough HP to play ${card.title}.` });
        return { kind: 'blocked' };
      }

      setXCostPick({ cardId, resource: 'hp', max, value: Math.min(1, max) });
      return { kind: 'deferred' };
    }

    const add = (card as any).addendum as undefined | { costSp?: number | 'X'; costHp?: number | 'X'; altDescription: string };

    if (!add || !isPlayerTurn || !state.canPlay) {
      if (cardNeedsDiscardPrereq(card)) {
        queuePlayAnimationAfterPrereq(cardId);
        return { kind: 'deferred' };
      }
      if (isAttackCard(card)) pendingAttackImpactShakeRef.current = true;
      return { kind: 'immediate', invokeVariant: resolvePlayInvokeVariant(cardId) };
    }

    const resource: 'sp' | 'hp' = (add as any)?.costHp != null ? 'hp' : 'sp';
    const costToken = (resource === 'hp' ? (add as any)?.costHp : (add as any)?.costSp) as (number | 'X' | undefined);

    // Player can only pay extra beyond the card's base cost for that resource.
    const maxExtra = resource === 'hp'
      ? Math.max(0, state.player.hp - (card.cost?.hp || 0))
      : Math.max(0, state.player.sp - (card.cost?.sp || 0));
    if (maxExtra <= 0) {
      if (isAttackCard(card)) pendingAttackImpactShakeRef.current = true;
      return { kind: 'immediate', invokeVariant: resolvePlayInvokeVariant(cardId) };
    }

    if (costToken === 'X') {
      setAddendumPick({ cardId, variable: true, max: maxExtra, value: 0, resource });
      return { kind: 'deferred' };
    }

    const fixed = Math.max(0, Number(costToken) || 0);
    if (fixed <= 0 || fixed > maxExtra) {
      // Can't afford addendum or invalid config; play normally.
      if (isAttackCard(card)) pendingAttackImpactShakeRef.current = true;
      return { kind: 'immediate', invokeVariant: resolvePlayInvokeVariant(cardId) };
    }

    setAddendumPick({ cardId, variable: false, fixed, max: maxExtra, value: fixed, resource });
    return { kind: 'deferred' };
  }, [canAffordImmediateCard, canPayDiscardPrereq, cardNeedsDiscardPrereq, computeMaxAffordableX, computeMaxAffordableXHp, discardMode, getDiscardPrereqAmount, isPlayerTurn, queuePlayAnimationAfterPrereq, reshuffleMode, resolvePlayInvokeVariant, state.canPlay, state.hand, state.player.hp, state.player.sp]);

  const enemyStartPending = state.phase === 'enemy'
    && !!(state as any).enemyStartPending
    && !state.enemyAwaitingAck
    && !state.enemyIntent;
  const combatEndPendingResult = (() => {
    const pending = (state as any).combatEndPendingResult;
    return pending === 'reward' || pending === 'gameover' ? pending : null;
  })();
  const combatEndPending = combatEndPendingResult !== null;
  const autoFocusPending = state.phase === 'player' && !!(state as any).autoFocusPending;
  const playerVisualQueueIdle = React.useMemo(
    () => playerVisualTokensRef.current.size === 0 && floatingFeedbackActiveCountRef.current === 0,
    [playerVisualGateTick]
  );
  const arePlayerVisualsIdleNow = React.useCallback(
    () => playerVisualTokensRef.current.size === 0 && floatingFeedbackActiveCountRef.current === 0,
    []
  );
  const allowPlayerTurnHandoffWhileCombatEndPending = state.phase === 'player' && combatEndPendingResult === 'reward';

  const enemyTurnHandoffKey = enemyStartPending ? `enemy:${state.turn}` : '';

  const turnHandoffAnchorStyle = React.useMemo(() => {
    const viewportRect = impactViewportRect ?? impactViewportRectRef.current;
    const portraitRect = portraitMaskRef.current?.getBoundingClientRect() ?? null;

    if (viewportRect && portraitRect && portraitFit.w > 0 && portraitFit.h > 0) {
      return {
        left: `${portraitRect.left + portraitFit.x + portraitFit.w * TURN_HANDOFF_BANNER.anchorXFactor}px`,
        top: `${portraitRect.top + portraitFit.y + portraitFit.h * TURN_HANDOFF_BANNER.anchorYFactor}px`,
        maxWidth: `${TURN_HANDOFF_BANNER.maxWidthPx}px`,
      } as React.CSSProperties;
    }

    if (typeof window !== 'undefined') {
      return {
        left: `${window.innerWidth * 0.5}px`,
        top: `${Math.max(108, window.innerHeight * 0.33)}px`,
        maxWidth: `${TURN_HANDOFF_BANNER.maxWidthPx}px`,
      } as React.CSSProperties;
    }

    return {
      left: '50vw',
      top: '33vh',
      maxWidth: `${TURN_HANDOFF_BANNER.maxWidthPx}px`,
    } as React.CSSProperties;
  }, [impactViewportRect, portraitFit.h, portraitFit.w, portraitFit.x, portraitFit.y]);

  const turnHandoffText = activeTurnHandoffBanner
    ? activeTurnHandoffBanner.kind === 'player'
      ? TURN_HANDOFF_BANNER.text.playerLabel
      : TURN_HANDOFF_BANNER.text.enemyLabel
    : '';

  const activeTurnHandoffSpriteConfig = React.useMemo(() => {
    if (!activeTurnHandoffBanner || activeTurnHandoffBanner.mode !== 'spritesheet') return null;
    return getTurnHandoffBannerSpritesheetConfig(activeTurnHandoffBanner.kind);
  }, [activeTurnHandoffBanner]);

  const turnHandoffSpriteFrameStyle = React.useMemo(() => {
    if (!activeTurnHandoffSpriteConfig) return undefined;

    const frame = Math.max(0, Math.min(activeTurnHandoffSpriteConfig.frameCount - 1, turnHandoffSpriteFrame));
    const column = frame % activeTurnHandoffSpriteConfig.columns;
    const row = Math.floor(frame / activeTurnHandoffSpriteConfig.columns);
    const width = activeTurnHandoffSpriteConfig.sizePx * (activeTurnHandoffSpriteConfig.frameWidthPx / Math.max(1, activeTurnHandoffSpriteConfig.frameHeightPx));
    const height = activeTurnHandoffSpriteConfig.sizePx;

    return {
      width: `${width}px`,
      height: `${height}px`,
      backgroundImage: `url(${activeTurnHandoffSpriteConfig.sheetUrl})`,
      backgroundSize: `${activeTurnHandoffSpriteConfig.columns * width}px ${activeTurnHandoffSpriteConfig.rows * height}px`,
      backgroundPosition: `${-column * width}px ${-row * height}px`,
    } as React.CSSProperties;
  }, [activeTurnHandoffSpriteConfig, turnHandoffSpriteFrame]);

  React.useEffect(() => {
    if (turnHandoffAnimationFrameRef.current) {
      window.cancelAnimationFrame(turnHandoffAnimationFrameRef.current);
      turnHandoffAnimationFrameRef.current = null;
    }

    if (!activeTurnHandoffBanner || activeTurnHandoffBanner.mode !== 'spritesheet') {
      setTurnHandoffSpriteFrame(0);
      return;
    }

    const spriteConfig = getTurnHandoffBannerSpritesheetConfig(activeTurnHandoffBanner.kind);
    const frameDurationMs = 1000 / Math.max(1, spriteConfig.fps);
    const animationDurationMs = Math.min(TURN_HANDOFF_BANNER.durationMs, spriteConfig.frameCount * frameDurationMs);
    const animationStart = performance.now();
    let lastFrame = -1;

    const tick = (now: number) => {
      const elapsed = Math.max(0, now - animationStart);
      const clampedElapsed = Math.min(animationDurationMs, elapsed);
      const nextFrame = Math.max(0, Math.min(spriteConfig.frameCount - 1, Math.floor(clampedElapsed / frameDurationMs)));
      if (nextFrame !== lastFrame) {
        lastFrame = nextFrame;
        setTurnHandoffSpriteFrame(nextFrame);
      }

      if (elapsed < animationDurationMs) {
        turnHandoffAnimationFrameRef.current = window.requestAnimationFrame(tick);
      } else {
        turnHandoffAnimationFrameRef.current = null;
      }
    };

    setTurnHandoffSpriteFrame(0);
    turnHandoffAnimationFrameRef.current = window.requestAnimationFrame(tick);

    return () => {
      if (turnHandoffAnimationFrameRef.current) {
        window.cancelAnimationFrame(turnHandoffAnimationFrameRef.current);
        turnHandoffAnimationFrameRef.current = null;
      }
    };
  }, [activeTurnHandoffBanner]);

  const turnHandoffParticles = React.useMemo(() => {
    const count = Math.max(0, TURN_HANDOFF_BANNER.particleCount);
    return Array.from({ length: count }, (_, index) => {
      const centeredIndex = index - (count - 1) / 2;
      const spreadBase = TURN_HANDOFF_BANNER.particleSpreadPx;
      const x = centeredIndex * (spreadBase / Math.max(1, count - 1 || 1));
      const y = (index % 2 === 0 ? -1 : 1) * (10 + (index % 3) * 7);
      const drift = (index % 2 === 0 ? 1 : -1) * (14 + index * 3);
      const delay = index * 90;
      const size = 2 + (index % 2);
      return {
        key: `turn-particle-${index}`,
        x,
        y,
        drift,
        delay,
        size,
      };
    });
  }, []);

  React.useEffect(() => {
    if (!combatEndPending) {
      combatEndReleaseInFlightRef.current = false;
      return;
    }
    if (allowPlayerTurnHandoffWhileCombatEndPending && pendingPlayerTurnHandoff) return;
    if (!arePlayerVisualsIdleNow() || combatEndReleaseInFlightRef.current || activeTurnHandoffBanner) return;
    combatEndReleaseInFlightRef.current = true;
    onAcknowledgeEnemy();
  }, [
    activeTurnHandoffBanner,
    allowPlayerTurnHandoffWhileCombatEndPending,
    arePlayerVisualsIdleNow,
    combatEndPending,
    onAcknowledgeEnemy,
    pendingPlayerTurnHandoff,
    playerVisualGateTick,
  ]);

  React.useEffect(() => {
    if (!autoFocusPending) {
      autoFocusReleaseInFlightRef.current = false;
      return;
    }
    if (!playerVisualQueueIdle || autoFocusReleaseInFlightRef.current || combatEndPending || enemyStartPending || turnHandoffLocked) return;
    autoFocusReleaseInFlightRef.current = true;
    onSkipTurn();
  }, [autoFocusPending, combatEndPending, enemyStartPending, onSkipTurn, playerVisualQueueIdle, turnHandoffLocked]);

  React.useEffect(() => {
    if (combatEndPending || activeTurnHandoffBanner) return;
    if (suppressInitialTurnHandoff) {
      enemyStartReleaseInFlightRef.current = false;
      return;
    }
    if (!enemyTurnHandoffKey || !playerVisualQueueIdle) {
      enemyStartReleaseInFlightRef.current = false;
      return;
    }
    if (lastShownEnemyTurnHandoffKeyRef.current === enemyTurnHandoffKey || enemyStartReleaseInFlightRef.current) return;
    enemyStartReleaseInFlightRef.current = true;
    lastShownEnemyTurnHandoffKeyRef.current = enemyTurnHandoffKey;
    setActiveTurnHandoffBanner({ key: enemyTurnHandoffKey, kind: 'enemy', mode: TURN_HANDOFF_BANNER.mode });
  }, [activeTurnHandoffBanner, combatEndPending, enemyTurnHandoffKey, playerVisualQueueIdle, suppressInitialTurnHandoff]);

  React.useEffect(() => {
    if ((combatEndPending && !allowPlayerTurnHandoffWhileCombatEndPending) || activeTurnHandoffBanner || suppressInitialTurnHandoff) return;
    if (!playerTurnHandoffKey || !playerVisualQueueIdle) return;
    if (lastShownPlayerTurnHandoffKeyRef.current === playerTurnHandoffKey) return;
    lastShownPlayerTurnHandoffKeyRef.current = playerTurnHandoffKey;
    setActiveTurnHandoffBanner({ key: playerTurnHandoffKey, kind: 'player', mode: TURN_HANDOFF_BANNER.mode });
  }, [activeTurnHandoffBanner, allowPlayerTurnHandoffWhileCombatEndPending, combatEndPending, playerTurnHandoffKey, playerVisualQueueIdle, suppressInitialTurnHandoff]);

  React.useEffect(() => {
    if (!activeTurnHandoffBanner) {
      if (turnHandoffTimerRef.current) {
        window.clearTimeout(turnHandoffTimerRef.current);
        turnHandoffTimerRef.current = null;
      }
      return;
    }

    if (turnHandoffTimerRef.current) {
      window.clearTimeout(turnHandoffTimerRef.current);
      turnHandoffTimerRef.current = null;
    }

    turnHandoffTimerRef.current = window.setTimeout(() => {
      const releaseKind = activeTurnHandoffBanner.kind;
      setActiveTurnHandoffBanner((current) => (current?.key === activeTurnHandoffBanner.key ? null : current));
      turnHandoffTimerRef.current = null;
      if (releaseKind === 'enemy') {
        enemyStartReleaseInFlightRef.current = false;
        onAcknowledgeEnemy();
      }
    }, TURN_HANDOFF_BANNER.durationMs);

    return () => {
      if (turnHandoffTimerRef.current) {
        window.clearTimeout(turnHandoffTimerRef.current);
        turnHandoffTimerRef.current = null;
      }
    };
  }, [activeTurnHandoffBanner, onAcknowledgeEnemy]);

  const showingEnemyCard = state.phase === 'enemy' && state.enemyAwaitingAck && !!state.enemyIntent;
  const showPeekPrompt = peekMode && !!state.pendingPeek && !choiceMode && playerVisualQueueIdle && !turnHandoffLocked;
  const showTopPickPrompt = topPickMode && !!(state as any).pendingTopPick && !choiceMode && playerVisualQueueIdle && !turnHandoffLocked;
  const showChoicePrompt = choiceMode && !!state.pendingChoice && !turnHandoffLocked;
  const showXPrompt = xMode && !!state.pendingX && !choiceMode && playerVisualQueueIdle && !turnHandoffLocked;
  const showTutorPrompt = tutorMode && !!state.pendingTutor && !choiceMode && playerVisualQueueIdle && !turnHandoffLocked;
  const showEnemyCardPrompt = showingEnemyCard && !choiceMode && playerVisualQueueIdle && !turnHandoffLocked;
  const showDiscardPrompt = discardMode && !choiceMode && playerVisualQueueIdle && !turnHandoffLocked;
  const enemyIntentPreview = state.phase === 'player' ? (state as any).enemyIntentPreview : null;
  const showEnemyIntentPreview = !!enemyIntentPreview && !!((state as any).relicMods?.revealEnemyIntentPreview);

  // Determine whether the player's hand UI should be disabled. Centralizing this
  // logic prevents duplicate inline expressions and makes the disabled prop stable.
  const showPlayerStunnedHandNotice =
    playerIsStunned &&
    isPlayerTurn &&
    !discardMode &&
    !peekMode &&
    !topPickMode &&
    !choiceMode &&
    !xMode &&
    !tutorMode &&
    !showingEnemyCard &&
    !turnHandoffLocked;

  const handDisabled =
    !!pendingRecastRequest ||
    (!discardMode && !isPlayerTurn) ||
    (!discardMode && (state.player?.stunned ?? 0) > 0) ||
    showingEnemyCard ||
    peekMode ||
    choiceMode ||
    xMode ||
    tutorMode ||
    !!xCostPick ||
    !!addendumPick ||
    turnHandoffLocked ||
    (!discardMode && !reshuffleMode && !state.canPlay);

  // Provide stable references for the currently chosen card in a choice prompt and
  // any card that is locked pending prerequisite plays. These props are passed
  // into the extracted bottom HUD so that Hand can highlight or disable cards
  // appropriately without recomputing these expressions on every render.
  const activeChoiceCardId: string | null = state.pendingChoice?.sourceId ?? null;
  const lockedCardId: string | null = (state as any).pendingPrereqPlay?.cardId ?? null;

  const portraitReady = portraitFit.w > 0 && portraitFit.h > 0;
  const enemyHitVfxBaseSize = React.useMemo(() => {
    if (!portraitReady) return 168;
    return Math.max(132, Math.min(portraitFit.w * 0.56, portraitFit.h * 0.56, 252));
  }, [portraitFit.h, portraitFit.w, portraitReady]);

  const enemyPreImpactFrameStyle = React.useMemo(() => {
    if (!enemyPreImpactVfxState || !portraitReady) return undefined;

    const frame = Math.max(0, Math.min(PREIMPACT_VFX.frameCount - 1, enemyPreImpactVfxState.frame));
    const column = frame % PREIMPACT_VFX.columns;
    const row = Math.floor(frame / PREIMPACT_VFX.columns);
    const size = Math.max(PREIMPACT_VFX.minSizePx, Math.min(portraitFit.w * PREIMPACT_VFX.sizeFactor, portraitFit.h * PREIMPACT_VFX.portraitHeightCapFactor, PREIMPACT_VFX.maxSizePx));

    return {
      left: portraitFit.w * PREIMPACT_VFX.centerXFactor,
      top: portraitFit.h * PREIMPACT_VFX.centerYFactor,
      width: size,
      height: size,
      backgroundImage: `url(${preimpactSheetPng})`,
      backgroundSize: `${size * PREIMPACT_VFX.columns}px ${size * PREIMPACT_VFX.rows}px`,
      backgroundPosition: `${-column * size}px ${-row * size}px`,
    } as React.CSSProperties;
  }, [enemyPreImpactVfxState, portraitFit.h, portraitFit.w, portraitReady]);

  const enemyHitVfxFrameStyle = React.useMemo(() => {
    if (!enemyImpactVfxState) return undefined;

    const frame = Math.max(0, Math.min(ENEMY_HIT_VFX.frameCount - 1, enemyImpactVfxState.frame));
    const column = frame % ENEMY_HIT_VFX.columns;
    const row = Math.floor(frame / ENEMY_HIT_VFX.columns);
    const width = enemyHitVfxBaseSize * 2.1;
    const height = enemyHitVfxBaseSize * 2.1;

    return {
      left: enemyImpactVfxState.x,
      top: enemyImpactVfxState.y,
      width,
      height,
      backgroundImage: `url(${enemyHitVfxSheetPng})`,
      backgroundSize: `${width * ENEMY_HIT_VFX.columns}px ${height * ENEMY_HIT_VFX.rows}px`,
      backgroundPosition: `${-column * width}px ${-row * height}px`,
    } as React.CSSProperties;
  }, [enemyHitVfxBaseSize, enemyImpactVfxState]);

  // (Side placement outside/inside is decided by the responsive layout effect above.)

  return (
    <div ref={sceneRef} className="scene" style={sceneStyle}>
      {!aggressiveLowFx && playerDamageFlashTick > 0 && (
        <div key={playerDamageFlashTick} className="combatDamageFlash" aria-hidden="true" />
      )}
      {!aggressiveLowFx && playerHealFlashTick > 0 && (
        <div key={playerHealFlashTick} className="combatHealFlash" aria-hidden="true" />
      )}
      {!aggressiveLowFx && playerSpFlashTick > 0 && (
        <div key={playerSpFlashTick} className="combatSpFlash" aria-hidden="true" />
      )}
      {!lowFx && !aggressiveLowFx && (
        <div className="combatShadowVignette" aria-hidden="true">
          <img
            className="combatShadowVignetteImage"
            src={shadowVignettePng}
            alt=""
            aria-hidden="true"
            draggable={false}
          />
        </div>
      )}

      <CombatBackdropFx
        lowFx={lowFx}
        aggressiveLowFx={aggressiveLowFx}
        combatTorchProgress={combatTorchProgress}
        combatTorchCorruption={combatTorchCorruption}
        combatLightRadius={combatLightRadius}
        combatMadness={combatMadness}
        combatVignetteOpacity={combatVignetteOpacity}
        combatVignettePulseOpacity={combatVignettePulseOpacity}
        combatSanityScreenFlicker={combatSanityScreenFlicker}
        combatSanityPeripheralShadow={combatSanityPeripheralShadow}
      />

      {typeof document !== 'undefined' && createPortal(
        <div
          className="combatImpactFeedbackLayer"
          aria-hidden="true"
          style={impactViewportRect
            ? {
                left: `${impactViewportRect.left}px`,
                top: `${impactViewportRect.top}px`,
                width: `${impactViewportRect.width}px`,
                height: `${impactViewportRect.height}px`,
              }
            : undefined}
        >
          {enemyImpactVfxState && enemyHitVfxFrameStyle && (
            <div
              key={`impact-vfx-${enemyImpactVfxState.tick}`}
              className="combatImpactVfxSheet"
              style={enemyHitVfxFrameStyle}
              aria-hidden="true"
            />
          )}
          {impactFeedbackItems.map((item) => (
            <div
              key={item.id}
              className="combatImpactFeedback"
              style={{ left: `${item.x}px`, top: `${item.y}px` }}
            >
              <div className="combatImpactFeedbackBody">
                <div className="combatImpactFeedbackNumber">{item.text}</div>
                {item.label && <div className="combatImpactFeedbackLabel">{item.label}</div>}
              </div>
            </div>
          ))}
        </div>,
        document.body,
      )}
      <div className="scenePortraitMedia scenePortraitFrame" aria-hidden="true" style={{ zIndex: 'auto' }}>
        {/*
          Portrait mask: clips any non-transparent/white corners in the asset
          without re-encoding the WebP (works for animated WebP too).
        */}
        <div
          ref={portraitMaskRef}
          className="scenePortraitMask"
          data-lunge-enemy-anchor="head"
          aria-hidden="true"
        >
          <img
            ref={portraitRef}
            onLoad={updatePortraitFit}
            className="scenePortraitImg sceneTileMedia"
            src={scenePortrait}
            alt=""
            aria-hidden="true"
            loading="eager"
            decoding="async"
            draggable={false}
          />
          {primeVesselDestroyedPortraitOverlays.map((overlaySrc, overlayIndex) => (
            <img
              key={`prime-vessel-portrait-overlay-${overlayIndex}`}
              className="scenePortraitImg sceneTileMedia"
              src={overlaySrc}
              alt=""
              aria-hidden="true"
              loading="eager"
              decoding="async"
              draggable={false}
              style={{ pointerEvents: 'none' }}
            />
          ))}
          {enemyHitTick > 0 && (
            <div
              key={`enemy-shake-flash-${enemyHitTick}`}
              className="enemyPortraitShakeFlash"
              aria-hidden="true"
            />
          )}
          {portraitReady && (
            <div
              className="scenePortraitBitmapFx"
              aria-hidden="true"
              style={{
                left: `${portraitFit.x}px`,
                top: `${portraitFit.y}px`,
                width: `${portraitFit.w}px`,
                height: `${portraitFit.h}px`,
              }}
            >
              {enemyPreImpactVfxState && enemyPreImpactFrameStyle && (
                <div
                  key={`preimpact-vfx-${enemyPreImpactVfxState.tick}`}
                  className="enemyPreImpactVfxSheet"
                  style={enemyPreImpactFrameStyle}
                  aria-hidden="true"
                />
              )}
              {enemyPortraitImpactFlashTick > 0 && (
                <div
                  key={`portrait-impact-flash-${enemyPortraitImpactFlashTick}`}
                  className="enemyPortraitImpactFlash"
                  aria-hidden="true"
                />
              )}
            </div>
          )}
        </div>

        {/* Enemy HUD (HP/Armor) + enemy status chips are anchored to the rendered portrait. */}
        <div className="scenePortraitOverlay" aria-hidden="true" style={{ zIndex: 1 }}>
          <div
            ref={hudRef}
            className={hudInside ? 'enemyHudOverlay enemyHudInside' : 'enemyHudOverlay'}
            aria-label="Enemy stats"
            style={portraitReady ? ({ left: portraitFit.x, top: portraitFit.y } as React.CSSProperties) : undefined}
          >
            <div className="enemyHudInner">
              <div className="enemyHudInnerScale">
                <img className="enemyHudImage" src={enemyUI} alt="" aria-hidden="true" draggable={false} />
                <div className="enemyHudStat enemyHudHp" data-feedback-anchor="enemyHp" aria-hidden="true">{state.enemy.hp}</div>
                <div className="enemyHudStat enemyHudArmor" data-feedback-anchor="enemyArmor" aria-hidden="true">{state.enemy.armor}</div>
                <div className="enemyCombatFeedbackLayer" aria-hidden="true">
                  {enemyFloatingFeedbackItems.map((item) => (
                    <div
                      key={item.id}
                      className={[
                        'combatFloatingFeedback',
                        item.anchor === 'enemyHp' ? 'combatFloatingFeedback--enemyHp' : 'combatFloatingFeedback--enemyArmor',
                        `combatFloatingFeedback--${item.tone}`,
                      ].join(' ')}
                      style={{ ['--combat-feedback-offset-x' as any]: `${item.offsetX}px` }}
                    >
                      {item.text}
                    </div>
                  ))}
                </div>
                {showEnemyIntentPreview && enemyIntentPreview && (
                  <div
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '100%',
                      transform: 'translate(-50%, 10px)',
                      padding: '6px 10px',
                      borderRadius: 999,
                      border: '1px solid rgba(245, 222, 179, 0.45)',
                      background: 'rgba(18, 12, 10, 0.88)',
                      color: '#f5deb3',
                      fontSize: 11,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 8px 18px rgba(0, 0, 0, 0.35)',
                      pointerEvents: 'none',
                    }}
                    aria-label="Enemy intent preview"
                  >
                    Intent: {enemyIntentPreview.title}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            ref={chipsRef}
            className={chipsInside ? 'enemyStatusColumnWrap enemyChipsInside' : 'enemyStatusColumnWrap'}
            aria-label="Enemy status effects"
            style={portraitReady ? ({ left: portraitFit.x + portraitFit.w, top: portraitFit.y } as React.CSSProperties) : undefined}
          >
            <StatusChips
              enemyStatus={enemyStatusSnapshot}
              scope="enemy"
              pulseMap={statusPulseMap}
            />
          </div>
        </div>
      </div>

      {!aggressiveLowFx && (
        <div className="combatBackgroundOverlay" aria-hidden="true">
          <div className="combatBackgroundOverlayInner">
            <img
              className="combatBackgroundOverlayImage"
              src={combatUiOverlayPng}
              alt=""
              aria-hidden="true"
              draggable={false}
            />
            <div className="combatBackgroundOverlayFx combatBackgroundOverlayFx--glow" style={combatOverlayMaskStyle} />
            <div className="combatBackgroundOverlayFx combatBackgroundOverlayFx--shimmer" style={combatOverlayMaskStyle} />
            <div className="combatBackgroundOverlayFx combatBackgroundOverlayFx--bloom" style={combatOverlayMaskStyle} />
          </div>
        </div>
      )}

      <Toast message={toast} onDone={() => setToast(null)} />


{showPeekPrompt && state.pendingPeek && (
  <div className="modalBackdrop" role="dialog" aria-modal="true">
    <div className="modalContent">
      <div className="modalTitle">Reorder top cards</div>
      {state.pendingPeek.cards.length > 0 ? (
        <div className="modalHint">{state.pendingPeek.kind === 'any' ? `Arrange the top ${state.pendingPeek.cards.length} deck card(s), then confirm.` : `Arrange the top ${state.pendingPeek.cards.length} ${state.pendingPeek.kind} card(s), then confirm.`}</div>
      ) : (
        <div className="modalHint">{state.pendingPeek.kind === 'any' ? 'You have no cards in your deck.' : `You have no cards in your ${state.pendingPeek.kind} deck.`}</div>
      )}

      <div className="peekList">
        {peekOrder.map((id) => {
          const card = state.pendingPeek!.cards.find((c) => c.id === id);
          if (!card) return null;
          return (
            <div key={id} className="peekRow">
              <div className="peekButtons">
                <button className="btnTiny" onClick={() => movePeek(id, -1)} aria-label="Move up">▲</button>
                <button className="btnTiny" onClick={() => movePeek(id, +1)} aria-label="Move down">▼</button>
              </div>
              <div className="peekCard">
                <CardView
                  card={card}
                  disabled
                  physicalEscalation={state.physicalEscalation}
                  magicEscalation={state.magicEscalation}
                  magicChainStreak={state.magicChainStreak}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="modalActions">
        {peekOrder.length === 0 ? (
          <button className="btn" onClick={() => onConfirmPeek([])} type="button">Close</button>
        ) : (
          <>
            <button className="btn" onClick={() => onConfirmPeek(peekOrder)} type="button">Confirm order</button>
            <button
              className="btn btnSecondary"
              onClick={() => onConfirmPeek(state.pendingPeek!.cards.map((c) => c.id))}
              type="button"
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  </div>

)}

{showTopPickPrompt && (state as any).pendingTopPick && (
  <div className="modalBackdrop" role="dialog" aria-modal="true">
    <div className="modalContent">
      <div className="modalTitle">Pick 1 card</div>
      {(() => {
        const pending = (state as any).pendingTopPick as { kind: 'physical' | 'magic' | 'any'; cards: any[] };
        const kind = pending.kind;
        const cards = pending.cards || [];
        const empty = cards.length === 0;

        return (
          <>
            <div className="modalHint">
              {empty ? (kind === 'any' ? `You have no cards in your deck.` : `You have no cards in your ${kind} deck.`) : (kind === 'any' ? `Choose 1 of the top ${cards.length} deck card(s). The rest go to discard.` : `Choose 1 of the top ${cards.length} ${kind} card(s). The rest go to discard.`)}
            </div>

            {empty ? (
              <div className="modalActions" style={{ justifyContent: 'flex-end' }}>
                <button className="btn" type="button" onClick={() => onConfirmTopPick('')}>Close</button>
              </div>
            ) : (
              <div className="tutorGrid" role="list">
                {cards.map((c: any) => (
                  <div key={c.id} className="tutorItem" role="listitem">
                    <CardView
                      card={c}
                      onClick={() => onConfirmTopPick(c.id)}
                      physicalEscalation={state.physicalEscalation}
                      magicEscalation={state.magicEscalation}
                      magicChainStreak={state.magicChainStreak}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        );
      })()}
    </div>
  </div>
)}




{showChoicePrompt && state.pendingChoice && (
  <div className="modalBackdrop modalBackdrop--choice" role="dialog" aria-modal="true">
    <div className="modalContent modalContent--choice">
      <div className="modalTitle">Choose one</div>
      <div className="modalHint">{state.pendingChoice.sourceTitle}</div>
      <div className="choiceButtons">
        {state.pendingChoice.options.map((opt, i) => (
          <button key={i} className="btn" type="button" onClick={() => onConfirmChoice(i)}>
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  </div>
)}


{showXPrompt && state.pendingX && (
  <div className="modalBackdrop" role="dialog" aria-modal="true">
    <div className="modalContent">
      <div className="modalTitle">Choose X</div>
      <div className="modalHint">
        {state.pendingX.sourceTitle} — Lose X {state.pendingX.from.toUpperCase()} • Gain X {state.pendingX.to.toUpperCase()}
      </div>

      <div style={{ marginTop: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, opacity: 0.9 }}>
          <span>X</span>
          <span>{Math.max(0, Math.min(state.pendingX.max, xValue))} / {state.pendingX.max}</span>
        </div>
        <input
          type="range"
          min={0}
          max={state.pendingX.max}
          value={Math.max(0, Math.min(state.pendingX.max, xValue))}
          onChange={(e) => setXValue(Math.max(0, Math.min(state.pendingX!.max, Number(e.target.value) || 0)))}
          style={{ width: '100%' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, opacity: 0.85, marginTop: 6 }}>
          <span>Current HP: {state.player.hp}</span>
          <span>Current SP: {state.player.sp}</span>
        </div>
      </div>

      <div className="modalActions" style={{ justifyContent: 'flex-end' }}>
        <button className="btn" type="button" onClick={() => onConfirmX(Math.max(0, Math.min(state.pendingX!.max, xValue)))}>
          Confirm X
        </button>
      </div>
    </div>
  </div>
)}


{showTutorPrompt && state.pendingTutor && (
  <div className="modalBackdrop" role="dialog" aria-modal="true">
    <div className="modalContent">
      {(() => {
        const kind = state.pendingTutor!.kind;
        const zone = state.pendingTutor!.zone === 'discard' ? 'discard' : 'deck';
        const unifiedPile = zone === 'discard'
          ? (Array.isArray((state as any).discard) ? (state as any).discard : [...state.physicalDiscard, ...state.magicDiscard])
          : (Array.isArray((state as any).deck) ? (state as any).deck : [...state.physicalDeck, ...state.magicDeck]);
        const tutorCards = kind === 'physical' || kind === 'magic'
          ? unifiedPile.filter((c: any) => c.kind === kind)
          : unifiedPile;
        const empty = tutorCards.length === 0;

        return (
          <>
            <div className="modalTitle">{zone === 'discard' ? 'Search discard pile' : 'Search deck'}</div>
            <div className="modalHint">
              {empty
                ? (kind === 'any'
                    ? `You have no cards in your ${zone === 'discard' ? 'discard pile' : 'deck'}.`
                    : `You have no cards in your ${kind} ${zone === 'discard' ? 'discard pile' : 'deck'}.`)
                : (kind === 'any'
                    ? `Pick a card from your ${zone === 'discard' ? 'discard pile' : 'deck'}.`
                    : `Pick a card from your ${kind} ${zone === 'discard' ? 'discard pile' : 'deck'}.`)}
            </div>

            {empty ? (
              <div className="modalActions" style={{ justifyContent: 'flex-end' }}>
                <button className="btn" type="button" onClick={() => onConfirmTutor('')}>Close</button>
              </div>
            ) : (
              <div className="tutorGrid" role="list">
                {tutorCards.map((c: any) => (
                  <div key={c.id} className="tutorItem" role="listitem">
                    <CardView
                      card={c}
                      onClick={() => onConfirmTutor(c.id)}
                      physicalEscalation={state.physicalEscalation}
                      magicEscalation={state.magicEscalation}
                      magicChainStreak={state.magicChainStreak}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        );
      })()}
    </div>
  </div>
)}


      {showEnemyCardPrompt && state.enemyIntent && typeof document !== 'undefined' && createPortal(
        <div className="enemyPlayPromptRoot">
          <div
            className="enemyPlayPromptBackdrop"
            onClick={onAcknowledgeEnemy}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onAcknowledgeEnemy();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Enemy plays a card. Click anywhere to continue."
          >
            <div
              className="enemyPlayPromptCard"
              onClick={(e) => {
                e.stopPropagation();
                onAcknowledgeEnemy();
              }}
            >
              <CardView
                card={state.enemyIntent}
                physicalEscalation={state.physicalEscalation}
                magicEscalation={state.magicEscalation}
                magicChainStreak={state.magicChainStreak}
              />
            </div>
          </div>
        </div>,
        document.body,
      )}

      {activeTurnHandoffBanner && typeof document !== 'undefined' && createPortal(
        <div
          className={[
            'turnHandoffBanner',
            activeTurnHandoffBanner.kind === 'player' ? 'turnHandoffBanner--player' : 'turnHandoffBanner--enemy',
            activeTurnHandoffBanner.mode === 'spritesheet' ? 'turnHandoffBanner--spritesheet' : 'turnHandoffBanner--text',
          ].join(' ')}
          role="status"
          aria-live="polite"
          aria-label={turnHandoffText}
          style={{
            ...turnHandoffAnchorStyle,
            ['--turn-handoff-duration-ms' as any]: `${TURN_HANDOFF_BANNER.durationMs}ms`,
            ['--turn-handoff-fade-ms' as any]: `${TURN_HANDOFF_BANNER.fadeOutMs}ms`,
            ['--turn-handoff-glow-opacity' as any]: TURN_HANDOFF_BANNER.glowOpacity,
            ['--turn-handoff-font-family' as any]: TURN_HANDOFF_BANNER.text.fontFamily,
            ['--turn-handoff-letter-spacing' as any]: `${TURN_HANDOFF_BANNER.text.letterSpacingEm}em`,
            ['--turn-handoff-font-size-desktop' as any]: `${TURN_HANDOFF_BANNER.text.desktopFontSizePx}px`,
            ['--turn-handoff-font-size-mobile' as any]: `${TURN_HANDOFF_BANNER.text.mobileFontSizePx}px`,
            ['--turn-handoff-stroke-width' as any]: `${TURN_HANDOFF_BANNER.text.strokeWidthPx}px`,
          }}
        >
          <div className="turnHandoffBannerGlow" aria-hidden="true" />
          <div className="turnHandoffBannerParticles" aria-hidden="true">
            {turnHandoffParticles.map((particle) => (
              <span
                key={particle.key}
                className="turnHandoffBannerParticle"
                style={{
                  ['--turn-particle-x' as any]: `${particle.x}px`,
                  ['--turn-particle-y' as any]: `${particle.y}px`,
                  ['--turn-particle-drift' as any]: `${particle.drift}px`,
                  ['--turn-particle-delay' as any]: `${particle.delay}ms`,
                  ['--turn-particle-size' as any]: `${particle.size}px`,
                }}
              />
            ))}
          </div>
          {activeTurnHandoffBanner.mode === 'text' ? (
            <div className="turnHandoffBannerText">{turnHandoffText}</div>
          ) : (
            <div className="turnHandoffBannerSpriteStage" aria-hidden="true">
              <div className="turnHandoffBannerSprite" style={turnHandoffSpriteFrameStyle} />
            </div>
          )}
        </div>,
        document.body,
      )}

      <div className="topBar">
        <div className="topBarSpacer" aria-hidden="true" />
        <div className="turn">
          <span className="turnEnemyName">{enemyName}</span>
          <span className="turnSeparator" aria-hidden="true">·</span>
          <span className="turnCount">Turn {state.turn}</span>
        </div>
        <div className="actions">
          {onExitToDungeon && (
            <button onClick={onExitToDungeon} type="button">
              {exitLabel ?? 'Escape combat'}
            </button>
          )}
          <button onClick={onDeckBuilder} type="button">Legendarium</button>
          <button onClick={onReload} type="button">Reload</button>
        </div>
      </div>

      {showDiscardPrompt && (
        <div className="discardBanner" role="status" aria-live="polite">
          <div className="discardTitle">Discard required:</div>
          <div className="discardText">Click {state.pendingDiscard!.remaining} card(s) in your hand to discard them.</div>
        </div>
      )}

      {typeof document !== 'undefined' && activeDecreeBanner && isMonsignor && state.phase !== 'loading' && activeDecreeBanner.key === currentMonsignorDecreeBannerKey && createPortal(
        <div className="decreeBanner" role="status" aria-live="polite" key={activeDecreeBanner.key}>
          <img
            src={decreeBannerImg}
            alt=""
            aria-hidden="true"
            draggable={false}
            className="decreeBannerImage"
          />
          <div className="decreeBannerTitle">Decree of Restraint</div>
          <div className="decreeBannerTextWrap" ref={decreeBannerTextWrapRef}>
            <div
              className="decreeBannerText"
              ref={decreeBannerTextRef}
              style={decreeBannerTextFontPx ? { fontSize: `${decreeBannerTextFontPx}px` } : undefined}
            >
              {activeDecreeBanner.text}
            </div>
          </div>
        </div>,
        document.body
      )}

      <div className="center">
        <div className="enemyArea">
          {/* Intentionally empty: enemy UI is now rendered as an overlay on the portrait. */}
        </div>

        <CombatLog lines={state.log} />
      </div>


      <div ref={bottomRef} className="bottom" style={{ background: 'transparent' }}>
        {/* Bottom-center combat UI (uses src/combat/assets/combat_UI.png in the main repo). */}
<div className="combatUIWrap">
  <CombatUI
  hp={state.player.hp}
  sp={state.player.sp}
  maxHp={liveMaxHp}
  maxSp={liveMaxSp}
  deckCount={combatDeckCount}
  discardCount={combatDiscardCount}
  armor={state.player.armor}
  reflectPct={state.player.reflectPct}
  spPulseTick={playerSpPulseTick}
  />

  <div className="combatUIFeedbackLayer" aria-hidden="true">
    {playerFloatingFeedbackItems.map((item) => (
      <div
        key={item.id}
        className={[
          'combatFloatingFeedback',
          item.anchor === 'playerHp'
            ? 'combatFloatingFeedback--playerHp'
            : item.anchor === 'playerSp'
              ? 'combatFloatingFeedback--playerSp'
              : 'combatFloatingFeedback--playerArmor',
          `combatFloatingFeedback--${item.tone}`,
        ].join(' ')}
        style={{ ['--combat-feedback-offset-x' as any]: `${item.offsetX}px` }}
      >
        {item.text}
      </div>
    ))}
  </div>

  {/* Desktop-only (CSS) overlay: Recast left of Deck, Focus right of Discard */}
  {canTakeTurnAction && (
    <div className="combatTurnActions" aria-label="Turn actions (desktop)">
      {!reshuffleMode && (
        <>

          <div className="combatTurnBtnWrap combatTurnBtnWrap--recast">
  <button
    className="btn combatTurnBtn combatTurnBtn--recast"
    type="button"
    disabled={playerIsStunned || !!pendingRecastRequest}
    onClick={() => {
      setReshuffleMode(true);
      setReshufflePick([]);
    }}
  >
    <img
      className="combatTurnBtnTopIcon combatTurnBtnTopIcon--recast"
      src={recastPng}
      alt=""
      aria-hidden="true"
      draggable={false}
    />
    <span className="combatTurnBtnText">Recast</span>
  </button>

  <div className="combatTurnTooltip" role="tooltip">
    Reshuffle your hand and discard pile with your deck and redraw -1 cards.
  </div>
  </div>

  <div className="combatTurnBtnWrap combatTurnBtnWrap--focus">
  <button
    className="btn combatTurnBtn combatTurnBtn--focus"
    type="button"
    disabled={!!pendingRecastRequest}
    onClick={() => {
      setReshuffleMode(false);
      setReshufflePick([]);
      onSkipTurn();
    }}
  >
    <img
      className="combatTurnBtnTopIcon combatTurnBtnTopIcon--focus"
      src={focusPng}
      alt=""
      aria-hidden="true"
      draggable={false}
    />
    <span className="combatTurnBtnText">Focus</span>
  </button>

  <div className="combatTurnTooltip" role="tooltip">
    Skip your turn and recover 10 SP.
  </div>
  </div>
        </>
      )}

      {reshuffleMode && (
  <>
    <div className="combatTurnBtnWrap combatTurnBtnWrap--recast">
      <button
        className="btn combatTurnBtn combatTurnBtn--recast"
        type="button"
        disabled={reshufflePick.length === 0 || !!pendingRecastRequest}
        onClick={() => {
          commitRecastSelection(reshufflePick.slice());
        }}
      >
        Confirm recast ({reshufflePick.length})
      </button>
    </div>

    <div className="combatTurnBtnWrap combatTurnBtnWrap--focus">
      <button
        className="btn combatTurnBtn combatTurnBtn--focus"
        type="button"
        disabled={!!pendingRecastRequest}
        onClick={() => {
          setReshuffleMode(false);
          setReshufflePick([]);
        }}
      >
        Cancel
      </button>
    </div>
  </>
  )}
    </div>
  )}

<div className="combatUIHoverLayer" aria-hidden="true">
  <div className="combatTurnTooltip combatUIHoverTooltip" role="tooltip">
    <div className="combatUIHoverTooltipLine">HP: {state.player.hp}/{liveMaxHp}</div>
    <div className="combatUIHoverTooltipLine">SP: {state.player.sp}/{liveMaxSp}</div>
    <div className="combatUIHoverTooltipLine">Max. Armor: {liveMaxHp}</div>
    <div className="combatUIHoverTooltipLine">Max. Reflect: {liveMaxHp}</div>
    <div className="combatUIHoverTooltipNote">
      Armor and Reflect are capped at your maximum Health Points.
    </div>
  </div>
</div>
  
</div>

{/* Player status chips: curved arc between the combat UI and the hand fan. */}
<div className="playerStatusArcWrap" aria-hidden="true">
  <StatusChips
    playerStatus={playerStatusSnapshot}
    scope="player"
    layout="arc"
    pulseMap={statusPulseMap}
  />
</div>

{canTakeTurnAction && (
  <div className="handActionBar" aria-label="Turn actions">
    {!reshuffleMode && (
      <>
        <div className="combatTurnBtnWrap">
  <button
  className="btn"
  type="button"
  disabled={playerIsStunned || !!pendingRecastRequest}
  onClick={() => {
    setReshuffleMode(true);
    setReshufflePick([]);
  }}
>
  Recast
</button>

<button
  className="btn"
  type="button"
  disabled={!!pendingRecastRequest}
  onClick={() => {
    setReshuffleMode(false);
    setReshufflePick([]);
    onSkipTurn();
  }}
>
  Focus
</button>
</div>
      </>
    )}

    {reshuffleMode && (
      <>
        <button
          className="btn"
          type="button"
          disabled={reshufflePick.length === 0 || !!pendingRecastRequest}
          onClick={() => {
            commitRecastSelection(reshufflePick.slice());
          }}
        >
          Confirm recast ({reshufflePick.length})
        </button>

        <button
          className="btn"
          type="button"
          disabled={!!pendingRecastRequest}
          onClick={() => {
            setReshuffleMode(false);
            setReshufflePick([]);
          }}
        >
          Cancel
        </button>
      </>
    )}
  </div>
)}

        {showPlayerStunnedHandNotice && (
          <div className="handStatusNotice handStatusNotice--stunned" role="status" aria-live="polite">
            <span className="handStatusNoticeLabel">Stunned - cannot play</span>
          </div>
        )}

        <Hand
          hand={state.hand}
          onPlay={onPlay}
          onRequestPlay={handlePlayRequest}
      deckCount={combatDeckCount}
          mode={discardMode ? 'discard' : 'play'}
          selectionMode={reshuffleMode}
          selectedIds={reshufflePick}
          onToggleSelect={toggleReshufflePick}
          activeChoiceCardId={state.pendingChoice?.sourceId ?? null}
          lockedCardId={(state as any).pendingPrereqPlay?.cardId ?? null}
      disabled={handDisabled}
          physicalEscalation={state.physicalEscalation}
          magicEscalation={state.magicEscalation}
          magicChainStreak={state.magicChainStreak}
        />
      </div>


      {xCostPick && (() => {
        const all = [state.hand.main.physical, state.hand.main.magic, ...state.hand.extra].filter(Boolean) as any[];
        const card = all.find((c) => c.id === xCostPick.cardId);
        if (!card) return null;

        const max = xCostPick.max;
        const x = Math.max(1, Math.min(max, xCostPick.value));
        const resource = xCostPick.resource;
        const effective = resource === 'hp'
          ? computeEffectiveHpCostForX(card, x)
          : computeEffectiveSpCostForX(card, x);
        const afford = resource === 'hp' ? state.player.hp : state.player.sp;
        const resLabel = resource.toUpperCase();

        return (
          <div className="modalBackdrop" onClick={() => setXCostPick(null)} role="button" tabIndex={0}>
            <div className="modalContent" onClick={(e) => e.stopPropagation()}>
              <div className="modalTitle">Choose X</div>
              <div className="modalHint">{card.title} — X determines the card's effects. Cost modifiers still apply.</div>

              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 10 }}>
                <div style={{ flex: 1, opacity: 0.92 }}>
                  <div style={{ fontSize: 12, opacity: 0.85 }}>
                    Your {resLabel}: {afford} • Effective cost: {effective} {resLabel}
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, opacity: 0.9 }}>
                  <span>X</span>
                  <span>{x} / {max}</span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={max}
                  value={x}
                  onChange={(e) => setXCostPick((p) => p ? { ...p, value: Math.max(1, Math.min(max, Number(e.target.value) || 1)) } : p)}
                  style={{ width: '100%' }}
                />
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" onClick={() => setXCostPick(null)}>Cancel</button>
                <button
                  type="button"
                  disabled={effective > afford}
                  onClick={() => {
                    const chosen = Math.max(1, Math.min(max, x));
                    setXCostPick(null);
                    if (resource === 'hp') {
                      dispatchPlayAfterUiPrereqs(card.id, { addendumPay: 0, xPayHp: chosen });
                    } else {
                      dispatchPlayAfterUiPrereqs(card.id, { addendumPay: 0, xPaySp: chosen, xPayHp: 0 });
                    }
                  }}
                >
                  Play (X={x})
                </button>
              </div>
            </div>
          </div>
        );
      })()}


      {addendumPick && (() => {
        const all = [state.hand.main.physical, state.hand.main.magic, ...state.hand.extra].filter(Boolean) as any[];
        const card = all.find((c) => c.id === addendumPick.cardId);
        if (!card) return null;
        const max = addendumPick.max;
        const fixed = addendumPick.fixed;
        const resLabel = addendumPick.resource === 'hp' ? 'HP' : 'SP';
        const baseCost = addendumPick.resource === 'hp' ? (card.cost?.hp || 0) : (card.cost?.sp || 0);
        const playerRes = addendumPick.resource === 'hp' ? state.player.hp : state.player.sp;
        const pay = addendumPick.variable ? Math.max(0, Math.min(max, addendumPick.value)) : (fixed ?? 0);

        return (
          <div className="modalBackdrop" onClick={() => setAddendumPick(null)} role="button" tabIndex={0}>
            <div className="modalContent" onClick={(e) => e.stopPropagation()}>
              <div className="modalTitle">Addendum</div>
              <div className="modalHint">
                {addendumPick.variable
                  ? `Choose how much extra ${resLabel} to pay for the Addendum effect.`
                  : `Pay ${fixed} extra ${resLabel} to add the Addendum effect.`}
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 10 }}>
                <div style={{ flex: 1, opacity: 0.9 }}>
                  <div><b>{card.title}</b></div>
                  <div style={{ fontSize: 12, opacity: 0.85 }}>Your {resLabel}: {playerRes} • Base cost {resLabel}: {baseCost} • Extra available: {max}</div>
                </div>
              </div>

              {addendumPick.variable && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, opacity: 0.9 }}>
                    <span>Extra {resLabel}</span>
                    <span>{pay}</span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={max}
                    value={pay}
                    onChange={(e) => setAddendumPick((p) => p ? { ...p, value: Math.max(0, Math.min(max, Number(e.target.value) || 0)) } : p)}
                    style={{ width: '100%' }}
                  />
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" onClick={() => setAddendumPick(null)}>Cancel</button>
                <button type="button" onClick={() => { setAddendumPick(null); dispatchPlayAfterUiPrereqs(card.id, { addendumPay: 0, addendumResource: addendumPick.resource }); }}>
                  Play normally
                </button>
                <button
                  type="button"
                  disabled={addendumPick.variable ? pay <= 0 : false}
                  onClick={() => { setAddendumPick(null); dispatchPlayAfterUiPrereqs(card.id, { addendumPay: pay, xPaySp: 0, addendumResource: addendumPick.resource }); }}
                >
                  Play with Addendum {addendumPick.variable ? `(+${pay} ${resLabel})` : `(+${fixed} ${resLabel})`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Relic bar overlay. If relics are provided, display them at the bottom of the screen. */}
      {Array.isArray(relics) && relics.length > 0 && (
        <RelicBar
  relics={relics}
  pulseMap={relicPulseMap}
  feedbackMap={relicFeedbackMap}
/>
      )}
    </div>
  );
}